import { IProgram, SiteFormFields } from '@/Model/model';

import { ActionDropdownItem } from '@/commoncomponents/actionsdropdowncomponent/types';
import { CurrencyInputOptions } from 'vue-currency-input';
import { Vue } from 'vue-property-decorator';
import programConfigure from '@/store/modules/programConfigure';
import { Discount, DiscountType, AmountUnitType, TimeRangeType } from '@/Model/payments/types';

export default class APP_CONST extends Vue {
  /**
   * A collection of application features with their corresponding feature flag key.
   */
  public static readonly FEATURE_KEYS = {
    profilesAdminView: 'profiles-admin-view',
    customPayments: 'custom-payments',
    playground: 'playground',
    customContentCollections: 'custom-content-collections',
    automaticDiscounts: 'automatic-discounts',
    learningExperienceDirectAccess: 'release-staff-422-learning-experience-direct-access',
    enforceLearningExperienceDirectAccessForAllAccounts: 'experiment-all-accounts-enforce-lx-direct-access',
    sharedCollections: 'shared-collections',
    cmsLxEnhancements: 'release-staff-70-cms-lx-enhancements',
    singleSignOn: 'release-staff-423-single-sign-on',
    attendanceEnhancements: 'release-staff-435-attendance-enhancements',
    paymentPlans: 'payment-plans',
    staffCustomerPortal: 'staff-customer-portal',
  } as const;

  public static readonly APP_ROUTES = {
    ROOT: { name: 'Root', path: '/' },
    ACCOUNT: { name: 'Program', path: '/account' },
    ACCOUNT_TEAM: { name: 'Account Team', path: '/account/team' },
    ACCOUNT_PROGRAMS: { name: 'Account Programs', path: '/account/programs' },
    ACCOUNT_SITES: { name: 'Account Sites', path: '/account/sites' },
    ACCOUNT_PROGRAMS_SESSIONS: { name: 'Program Sessions', path: '/account/programs/sessions' },
    ACCOUNT_PROGRAM_SESSION_CONFIGURATION: { name: 'Program Session Configuration', path: '/account/programs/sessions/configure' },
    ACCOUNT_PARTICIPANTS: { name: 'Account Participants', path: '/account/participants' },
    ACCOUNT_CONTACTS: { name: 'Account Contacts', path: '/account/contacts' },
    ACCOUNT_PAYMENTS: { name: 'Account Payments', path: '/account/payments' },
    CMS: { name: 'Learning Resources', path: '/resources' },
    CMS_CATALOG: { name: 'Learning Catalog', path: '/resources/catalog', menuTitle: 'Learning library' },
    CMS_COURSE_LANDING: { name: 'Course Landing', path: '/resources/course' },
    CMS_ROUTINE_LANDING: { name: 'Routine Landing', path: '/resources/routine' },
    CMS_STRATEGY_LANDING: { name: 'Strategy Landing', path: '/resources/strategy' },
    CMS_MEDIA_RESOURCE_LANDING: { name: 'Media Resource Landing', path: '/resources/media' },
    CMS_MEDIA_RESOURCE_LONG_LANDING: { name: 'Media Resource Long Landing', path: '/resources/media-long' },
    CMS_VIDEO_RESOURCE_LANDING: { name: 'Video Resource Landing', path: '/resources/video' },
    CMS_VIEW_PDF_LANDING: { name: 'PDF View', path: '/resources/viewpdf' },
    CMS_ACTIVITY_LANDING: { name: 'Activity Landing', path: '/resources/activity' },
    CMS_ACTIVITY_BLAST_LANDING: { name: 'Activity Blast', path: '/resources/blast' },
    CMS_ACTIVITY_FITNESS_LANDING: { name: 'Activity fitness Landing', path: '/resources/fitness' },
    CMS_MY_CONTENT: { name: 'My Content', path: '/resources/dashboard', menuTitle: 'Learning launchpad' },
    CMS_LEARN_ABOUT_LANDING: { name: 'Learn About Landing', path: '/resources/learn-about' },
    CMS_CONTENT_PREVIEW_LANDING: { name: 'Content Preview Landing', path: '/resources/content-preview' },
    MY_COLLECTIONS: { name: 'My Collections', path: '/resources/my-collections', menuTitle: 'My collections' },
    SINGLE_COLLECTION: { name: 'Single Collection', path: '/resources/my-collections/:collectionId' },
  };
  public static readonly CURRENCY_OPTIONS: CurrencyInputOptions = {
    currency: 'USD',
    locale: 'en',
    allowNegative: false
  };
  public static readonly DAYS_IN_A_WEEK = 7;
  public static readonly oneDayInMilliseconds = 24 * 60 * 60 * 1000;

  public static readonly STYLE = {
    BOX_SHADOW: {
      ELEVATION_100: '0px 2px 8px 0px rgba(0, 0, 0, 0.12)'
    },
    COLOR: {
      BLUE_700: '#00797D',
      DANGER_700: '#DA2030',
      NEUTRAL_100: '#FFFFFF',
      NEUTRAL_200: '#F8F8F8',
      NEUTRAL_500: '#D0D0D0',
      NEUTRAL_600: '#767676',
      NEUTRAL_700: '#585858',
      NEUTRAL_900: '#2B2B2B',
      PRIMARY_700: '#546122',
      WARNING_700: '#B25E09'
    },
    FONT_FAMILY: {
      ARLY_FONT: 'Montserrat, sans-serif'
    },
    FONT_SIZE: {
      HEADLINE_300_LIGHT_SIZE: '24px',
      PARAGRAPH_200_HEAVY_SIZE: '16px',
      PARAGRAPH_200_LIGHT_SIZE: '16px',
      PARAGRAPH_300_HEAVY_SIZE: '14px',
      PARAGRAPH_300_LIGHT_SIZE: '14px',
      PARAGRAPH_400_HEAVY_SIZE: '12px'
    },
    FONT_WEIGHT: {
      PARAGRAPH_200_HEAVY_WEIGHT: '700',
      PARAGRAPH_300_HEAVY_WEIGHT: '700',
      PARAGRAPH_300_LIGHT_WEIGHT: '500',
      PARAGRAPH_400_HEAVY_WEIGHT: '700'
    },
    LINE_HEIGHT: {
      PARAGRAPH_200_HEAVY_LINE_HEIGHT: '24px',
      PARAGRAPH_300_HEAVY_LINE_HEIGHT: '20px',
      PARAGRAPH_300_LIGHT_LINE_HEIGHT: '20px'
    }
  };

  public static readonly UPLOAD_FILE_MB = 5 * 1024 * 1024;
  public static readonly LANGUAGE_ENGLISH = 'en';
  public static readonly LANGUAGE_SPANISH = 'es';
  public static readonly PROGRAMS = 'programs';
  public static readonly PROGRAM_ADMIN = 'programAdmin';
  public static readonly LICENSE = 'license';
  public static readonly LEARNING = 'Learning';
  public static readonly LEARNING_AND_RESOURCES =
    'Learning & Resources';
  public static readonly ACCOUNT_NAME = 'name';
  public static readonly AUTO_SAVE_INTERVAL: number = 20000;
  public static readonly MOBLIE_HEIGHT_SIXNINE: number = 69;
  public static readonly MOBLIE_HEIGHT_TWOFOURTYTWO: number = 242;
  public static readonly USER_TYPE_LABEL = [
    'BellXcel Super Admin',
    'BellXcel Admin',
    'BX Content Admin',
    'Account Admin',
    'Program Admin',
    'Session Admin',
    'Staff',
    '',
    'No Access'
  ];
  public static readonly VALID_EMAILS = [
    'magicsw.com',
    'magicedtech.com',
    'bellxcel.org',
    'experiencebell.org',
    'ruralsourcing.com',
    'teamsparq.com',
    '90op4gpa.mailosaur.net'
  ];
  public static readonly NON_BX_VALID_EMAILS = [];
  public static readonly ADD_SIGN = '';
  public static readonly DISABLE_PLACEHOLDER =
    'You must be an owner or collaborator to participate in discussions...';

  public static readonly CMS_USER_ACCESS_PATHS = [
    APP_CONST.APP_ROUTES.CMS.path,
    APP_CONST.APP_ROUTES.CMS_COURSE_LANDING.path,
    APP_CONST.APP_ROUTES.CMS_ROUTINE_LANDING.path,
    APP_CONST.APP_ROUTES.CMS_STRATEGY_LANDING.path,
    APP_CONST.APP_ROUTES.CMS_MEDIA_RESOURCE_LANDING.path,
    APP_CONST.APP_ROUTES.CMS_MEDIA_RESOURCE_LONG_LANDING.path,
    APP_CONST.APP_ROUTES.CMS_ACTIVITY_LANDING.path,
    APP_CONST.APP_ROUTES.CMS_ACTIVITY_FITNESS_LANDING.path,
    APP_CONST.APP_ROUTES.CMS_CATALOG.path,
    APP_CONST.APP_ROUTES.CMS_VIEW_PDF_LANDING.path,
    APP_CONST.APP_ROUTES.CMS_MY_CONTENT.path,
    APP_CONST.APP_ROUTES.CMS_VIDEO_RESOURCE_LANDING.path,
    APP_CONST.APP_ROUTES.CMS_LEARN_ABOUT_LANDING.path,
    APP_CONST.APP_ROUTES.CMS_CONTENT_PREVIEW_LANDING.path,
    APP_CONST.APP_ROUTES.MY_COLLECTIONS.path,
    APP_CONST.APP_ROUTES.SINGLE_COLLECTION.path,
  ];

  public static readonly USER_ACCESS_GROUP = [
    ['/partnerhome', ...APP_CONST.CMS_USER_ACCESS_PATHS, APP_CONST.APP_ROUTES.SINGLE_COLLECTION.name, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS_SESSIONS.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAM_SESSION_CONFIGURATION.path, APP_CONST.APP_ROUTES.ACCOUNT_TEAM.path, APP_CONST.APP_ROUTES.ACCOUNT_SITES.path, APP_CONST.APP_ROUTES.ACCOUNT_CONTACTS.path, APP_CONST.APP_ROUTES.ACCOUNT_PARTICIPANTS.path, '/account/program/sites', '/staffroster/new', '/account/program/site', '/account/program/sites/createsite'],
    // BX Super Admin
    ['/account/program/site', ...APP_CONST.CMS_USER_ACCESS_PATHS, APP_CONST.APP_ROUTES.SINGLE_COLLECTION.name, '/account/program/sites', '/account/program/sites/createsite', '/accounts/create', '/accounts/createprogram', '/accounts/edit', '/accounts/editprogram', '/analytics/familyEngagement', '/analytics/familyEngagement/family', '/analytics/familyEngagement/team', '/analytics/professionalLearning', '/analytics/quality', '/analytics/scholar', '/analytics/scholar/application', '/analytics/scholar/attendance', '/analytics/scholar/attendance/ada', '/analytics/scholar/attendance/completion', '/analytics/scholar/enrollment', '/analytics/scholar/retention', '/attendance', '/builder/build', '/builder/preview', '/builder/template', '/design-settings', '/design-settings/app-settings', '/design-settings/family-engagement', '/design-settings/family-engagement/design', '/roster', '/design-settings/family-engagement/preview', '/enroll', '/enroll/enrolled-scholars', '/enroll/manage/applications', '/enroll/manage/forms', '/enroll/submissions', '/families', '/families/guardian', '/families/scholar', '/family-details/contacts', '/family-details/guardian', '/family-details/programs', '/family-details/scholar', '/family-details/scholarDocument', '/home', '/partnerhome', '/planner', '/roster', '/roster/new', '/staffroster/new', '/users', APP_CONST.APP_ROUTES.ACCOUNT_PAYMENTS.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS_SESSIONS.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS.path, APP_CONST.APP_ROUTES.ACCOUNT_SITES.path, APP_CONST.APP_ROUTES.ACCOUNT_TEAM.path, APP_CONST.APP_ROUTES.ROOT.path, APP_CONST.APP_ROUTES.ACCOUNT_PARTICIPANTS.path, APP_CONST.APP_ROUTES.ACCOUNT_CONTACTS.path],
    // BX Admin
    ['/account/program/site', ...APP_CONST.CMS_USER_ACCESS_PATHS, APP_CONST.APP_ROUTES.SINGLE_COLLECTION.name, '/account/program/sites', '/account/program/sites/createsite', '/accounts/create', '/accounts/createprogram', '/accounts/edit', '/accounts/editprogram', '/analytics/familyEngagement', '/analytics/familyEngagement/family', '/analytics/familyEngagement/team', '/analytics/professionalLearning', '/analytics/quality', '/analytics/scholar', '/analytics/scholar/application', '/analytics/scholar/attendance', '/analytics/scholar/attendance/ada', '/analytics/scholar/attendance/completion', '/analytics/scholar/enrollment', '/analytics/scholar/retention', '/attendance', '/builder/build', '/builder/preview', '/builder/template', '/design-settings', '/design-settings/app-settings', '/design-settings/family-engagement', '/design-settings/family-engagement/design', '/roster', '/design-settings/family-engagement/preview', '/enroll', '/enroll/enrolled-scholars', '/enroll/manage/applications', '/enroll/manage/forms', '/enroll/submissions', '/families', '/families/guardian', '/families/scholar', '/family-details/contacts', '/family-details/guardian', '/family-details/programs', '/family-details/scholar', '/family-details/scholarDocument', '/home', '/partnerhome', '/planner', '/roster', '/roster/new', '/staffroster/new', '/users', APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS_SESSIONS.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAM_SESSION_CONFIGURATION.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS.path, APP_CONST.APP_ROUTES.ACCOUNT_SITES.path, APP_CONST.APP_ROUTES.ACCOUNT_TEAM.path, APP_CONST.APP_ROUTES.ROOT.path, APP_CONST.APP_ROUTES.ACCOUNT_PARTICIPANTS.path, APP_CONST.APP_ROUTES.ACCOUNT_CONTACTS.path],
    // Content Admin
    ['**'],
    // Account Admin
    ['/account/program/site', ...APP_CONST.CMS_USER_ACCESS_PATHS, APP_CONST.APP_ROUTES.SINGLE_COLLECTION.name, '/account/program/sites', '/account/program/sites/createsite', '/accounts/createprogram', '/accounts/editprogram', '/analytics/familyEngagement', '/analytics/familyEngagement/family', '/analytics/familyEngagement/team', '/analytics/professionalLearning', '/analytics/quality', '/analytics/scholar', '/analytics/scholar/application', '/analytics/scholar/attendance', '/analytics/scholar/attendance/ada', '/analytics/scholar/attendance/completion', '/analytics/scholar/enrollment', '/analytics/scholar/retention', '/application', '/attendance', '/builder/build', '/builder/preview', '/builder/template', '/design-settings', '/design-settings/app-settings', '/design-settings/family-engagement', '/design-settings/family-engagement/design', '/roster', '/design-settings/family-engagement/preview', '/enroll', '/enroll/enrolled-scholars', '/enroll/manage/applications', '/enroll/manage/forms', '/enroll/submissions', '/families', '/families/guardian', '/families/scholar', '/family-details/contacts', '/family-details/guardian', '/family-details/programs', '/family-details/scholar', '/family-details/scholarDocument', '/home', '/home', '/partnerhome', '/partnerhome', '/planner', '/planner', '/roster', '/roster/new', '/staffroster/new', '/users', APP_CONST.APP_ROUTES.ACCOUNT_PAYMENTS.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS_SESSIONS.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAM_SESSION_CONFIGURATION.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS.path, APP_CONST.APP_ROUTES.ACCOUNT_SITES.path, APP_CONST.APP_ROUTES.ACCOUNT_TEAM.path, APP_CONST.APP_ROUTES.ACCOUNT_PARTICIPANTS.path, APP_CONST.APP_ROUTES.ACCOUNT_CONTACTS.path],
    // Program Admin
    // Note: ACCOUNT_PAYMENTS is included in this to allow for the role ID hotswapping that happens when drilling down from higher level roles, e.g. Account Admin, however users with Program Admin as their highest role should NOT access the route (it should cancel the navigation instead)
    ['/account/program/site', ...APP_CONST.CMS_USER_ACCESS_PATHS, APP_CONST.APP_ROUTES.SINGLE_COLLECTION.name, '/account/program/sites', '/account/program/sites/createsite', '/accounts/createprogram', '/accounts/editprogram', '/analytics/familyEngagement', '/analytics/familyEngagement/family', '/analytics/familyEngagement/team', '/analytics/professionalLearning', '/analytics/quality', '/analytics/scholar', '/analytics/scholar/application', '/analytics/scholar/attendance', '/analytics/scholar/attendance/ada', '/analytics/scholar/attendance/completion', '/analytics/scholar/enrollment', '/analytics/scholar/retention', '/application', '/attendance', '/builder/build', '/builder/preview', '/builder/template', '/dashboard', '/roster', '/enroll', '/enroll/enrolled-scholars', '/enroll/manage/applications', '/enroll/manage/forms', '/enroll/submissions', '/families', '/families/guardian', '/families/scholar', '/family-details/contacts', '/family-details/guardian', '/family-details/scholar', '/home', '/home', '/manageprogram', '/partnerhome', '/partnerhome', '/planner', '/planner', '/roster', '/roster/new', '/staffroster/new', APP_CONST.APP_ROUTES.ACCOUNT_PAYMENTS.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS_SESSIONS.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS.path, APP_CONST.APP_ROUTES.ACCOUNT_SITES.path, APP_CONST.APP_ROUTES.ACCOUNT_TEAM.path, APP_CONST.APP_ROUTES.ROOT.path, APP_CONST.APP_ROUTES.ACCOUNT_PARTICIPANTS.path, APP_CONST.APP_ROUTES.ACCOUNT_CONTACTS.path],
    // Session Admin
    // Note: ACCOUNT_PAYMENTS is included in this to allow for the role ID hotswapping that happens when drilling down from higher level roles, e.g. Account Admin, however users with Session Admin as their highest role should NOT access the route (it should cancel the navigation instead)
    ['/account/program/site', ...APP_CONST.CMS_USER_ACCESS_PATHS, APP_CONST.APP_ROUTES.SINGLE_COLLECTION.name, '/account/program/sites', '/account/program/sites/createsite', '/accounts/createprogram', '/accounts/editprogram', '/analytics/familyEngagement', '/analytics/familyEngagement/family', '/analytics/familyEngagement/team', '/analytics/quality', '/analytics/scholar', '/analytics/scholar/application', '/analytics/scholar/attendance', '/analytics/scholar/attendance/ada', '/analytics/scholar/attendance/completion', '/analytics/scholar/enrollment', '/analytics/scholar/retention', '/attendance', '/dashboard', '/roster', '/family-details/scholar', '/home', '/home', '/manageprogram', '/partnerhome', '/partnerhome', '/planner', '/planner', '/roster', '/roster/new', '/roster/scholar-roster', '/staffroster', '/staffroster/new', APP_CONST.APP_ROUTES.ACCOUNT_PAYMENTS.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS_SESSIONS.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAM_SESSION_CONFIGURATION.path, APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS.path, APP_CONST.APP_ROUTES.ACCOUNT_SITES.path, APP_CONST.APP_ROUTES.ACCOUNT_TEAM.path, APP_CONST.APP_ROUTES.ROOT.path, APP_CONST.APP_ROUTES.ACCOUNT_CONTACTS, APP_CONST.APP_ROUTES.ACCOUNT_PARTICIPANTS],
    // Staff
    ['/analytics/scholar', ...APP_CONST.CMS_USER_ACCESS_PATHS, APP_CONST.APP_ROUTES.SINGLE_COLLECTION.name, '/analytics/scholar/application', '/analytics/scholar/attendance', '/analytics/scholar/attendance/ada', '/analytics/scholar/attendance/completion', '/analytics/scholar/enrollment', '/analytics/scholar/retention', '/attendance', '/dashboard', '/roster', '/home', '/home', '/partnerhome', '/partnerhome', '/planner', '/planner', '/roster', '/roster/new', '/roster/scholar-roster', '/staffroster', '/staffroster/new', APP_CONST.APP_ROUTES.ROOT.path],
  ];

  public static readonly LOGOUT_ERROR = 'Logout error:';
  public static readonly SUPER_ADMIN_USER = 'superadmin';
  public static readonly ADMIN_USER = 'admin';
  public static readonly REGULAR_USER = 'user';
  public static readonly LOGIN = 'login';
  public static readonly REGISTER = 'register';
  public static readonly CHANGE_PASSWORD = 'changePassword';
  public static readonly USERNAME = 'username';
  public static readonly SET_PASSWORD = 'SetPassword';
  public static readonly PASSWORD = 'password';
  public static readonly DEFAULTSELECTROLE = 'Select Role';

  public static readonly ROLE_TYPE_SUPER_ADMIN = 1;
  public static readonly ROLE_TYPE_BX_ADMIN = 2;
  public static readonly ROLE_TYPE_BX_CONTENT_ADMIN = 3;
  public static readonly ROLE_TYPE_ACCOUNT_ADMIN = 4;
  public static readonly ROLE_TYPE_PROGRAM_ADMIN = 5;
  public static readonly ROLE_TYPE_SESSION_ADMIN = 6;
  public static readonly ROLE_TYPE_STAFF = 7;
  public static readonly ROLE_TYPE_NONSYSTEM = 9;

  public static readonly USER_STATUS_INACTIVE = 2;
  public static readonly USER_STATUS_ACTIVE = 1;
  public static readonly MANAGE_STAFF = 'manage-staff';
  public static readonly MANAGE_SITE = 'manage-site';

  public static readonly STRENGTH_COLORS_LIST = [
    'green',
    'yellow',
    'red'
  ];
  public static readonly STRENGTH_FONTS_WEIGHT = [
    'Strong',
    'Medium',
    'Weak'
  ];
  public static readonly STYLE_GREEN = 'green';
  public static readonly STYLE_RED = 'red';
  public static readonly STYLE_YELLOW = 'yellow';

  public static readonly STRONG = 'Strong';
  public static readonly STRONG_UPPERCASE = 'STRONG';
  public static readonly MEDIUM = 'Medium';
  public static readonly WEAK = 'Weak';
  public static readonly TEXT = 'text';

  public static readonly HOUR = 24;
  public static readonly MINUTES = 60;
  public static readonly SECOND = 60;
  public static readonly MILISECOND = 1000;

  public static readonly SITENAMEMAXLENGTH = 100;
  public static readonly SITEADDRESSMAXLENGTH = 150;
  public static readonly SITECITYMAXLENGTH = 50;

  //message
  public static readonly LOGIN_SUCCESS = 'login success';
  public static readonly LOGIN_FAIL = 'login fail';
  public static readonly AUTH_TOKEN = 'auth_token';

  public static readonly HIDDEN = 'hidden';
  public static readonly VISIBLE = 'visible';
  public static readonly TOOL_TIP = 'tooltip';
  public static readonly TOOL_TIP_CLOSE = 'tooltip-close';
  public static readonly AUTH_DATA: string = 'auth-data';
  public static readonly IDB_ACCOUNT_LIST: string =
    'account-list';
  public static readonly IDB_PROFILE_INFO: string = 'profile';
  public static readonly IDB_NEW_ACCOUNT_INFO: string =
    'Account';
  public static readonly SUCCESS_RESPONSE_STATUS: string = 'OK';
  public static readonly STAFF_DUPLICATE =
    'Staff with same details already exist for the same Site/Session.';

  public static readonly TRUE: boolean = true;
  public static readonly FALSE: boolean = false;
  public static readonly BLANK: string = '';
  public static readonly NO_ACTION: string = 'noAction';

  public static readonly MINUS_ONE: number = -1;
  public static readonly ZERO: number = 0;
  public static readonly ONE: number = 1;
  public static readonly TWO: number = 2;
  public static readonly THREE: number = 3;
  public static readonly FOUR: number = 4;
  public static readonly FIVE: number = 5;
  public static readonly SIX: number = 6;
  public static readonly SEVEN: number = 7;
  public static readonly EIGHT: number = 8;
  public static readonly NINE: number = 9;
  public static readonly TEN: number = 10;
  public static readonly FOURTEEN: number = 14;
  public static readonly SIXTEEN: number = 16;
  public static readonly THIRTY_FOUR: number = 34;
  public static readonly THIRTY_FIVE: number = 35;
  public static readonly FORTY: number = 40;
  public static readonly FOURTY_FIVE: number = 45;
  public static readonly FIFTY: number = 50;
  public static readonly SEVENTY_FIVE: number = 75;
  public static readonly SEVENTY_SIX: number = 76;
  public static readonly HUNDRED: number = 100;
  public static readonly ONE_HUNDRED_FORTY: number = 140;
  public static readonly TWO_TWENTY_SIX: number = 226;
  public static readonly TWENTY_FIVE: number = 25;
  public static readonly FIVE_HUNDRED_FIFTY: number = 550;

  public static readonly RESPONSE_200: number = 200;
  public static readonly RESPONSE_204: number = 204;
  public static readonly RESPONSE_400: number = 400;
  public static readonly RESPONSE_406: number = 406;
  public static readonly RESPONSE_500: number = 500;
  public static readonly RESPONSE_409: number = 409;
  public static readonly EVENT_CLOSE: string = 'close';
  public static readonly EVENT_CLICK: string = 'click';

  public static readonly USER_ID: string = 'user_id';
  public static readonly PHONE_NO: string =
    'Phone number (XXX) XXX-XXXX';

  public static readonly KEY_8: number = 8;
  public static readonly KEY_TAB: number = 9;
  public static readonly KEY_ENTER: number = 13;
  public static readonly KEY_16: number = 16;
  public static readonly KEY_ESCAPE: number = 27;
  public static readonly KEY_SPACE: number = 32;
  public static readonly KEY_35: number = 35;
  public static readonly KEY_36: number = 36;
  public static readonly KEY_ARROW_UP: number = 38;
  public static readonly KEY_ARROW_DOWN: number = 40;
  public static readonly KEY_41: number = 41;
  public static readonly KEY_46: number = 46;
  public static readonly KEY_48: number = 48;
  public static readonly KEY_57: number = 57;
  public static readonly KEY_65: number = 65;
  public static readonly KEY_67: number = 67;
  public static readonly KEY_68: number = 68;
  public static readonly KEY_86: number = 86;
  public static readonly KEY_88: number = 88;
  public static readonly KEY_90: number = 90;
  public static readonly KEY_96: number = 96;
  public static readonly KEY_105: number = 105;
  public static readonly KEY_210: number = 210;
  public static readonly ONE_HUNDRED_SIXTEEN: number = 116;
  public static readonly THREE_HUNDRED_TWENTY: number = 320;

  public static readonly ACCOUNT_ADMIN = 'Account Admin';
  public static readonly PROGRAAM_ADMIN = 'Program Admin';
  public static readonly SESSION_ADMIN = 'Session Admin';
  public static readonly STAFF = 'Staff';
  public static readonly NO_ACCESS = 'No Access';
  public static readonly INVITEE_EXPIRED = 'Invite Expired';
  public static readonly INVITEE_PENDING = 'Invite Pending';
  public static readonly SUPER_USER_DRILLDOWN =
    'super_user_drilldown';
  public static readonly REVOKE_DATE = 'revokeDate';
  public static readonly FIRST_NAME = 'firstName';
  public static readonly LAST_NAME = 'lastName';
  public static readonly PROGRAM_NAME = 'programName';
  public static readonly SITE_NAME = 'siteName';
  public static readonly ACCOUNT_LIST_NAME = 'accountName';
  public static readonly DEFAULT_TABLE_SEARCH_ROWS = 25;

  //accounts
  public static readonly ACCOUNT_SORT_LIST = [
    {
      'Account Name': 'Account Name',
      type: 'string',
      key: 'accountName'
    },
    {
      'Account Admin': 'Account Admin',
      type: 'string',
      key: 'firstName'
    },
    {
      'No. of Programs': 'No. of Programs',
      type: 'number',
      key: 'programCount'
    }
  ];
  public static readonly ACCOUNT_KEY = [
    'accountName',
    'firstName',
    'programCount'
  ];
  public static readonly ACCOUNT_ACTION = {
    action: 'account',
    add: 'Add Program',
    edit: 'Edit Account',
    viewDetails: 'View Details',
    editUser: 'Edit User',
    removeUser: 'Remove User',
    removeUserImage: 'ic_delete.svg',
    editUserImage: 'ic_edit.svg',
    viewUserImage: 'ic_view.svg',
    disable: 'Disable Account',
    cancel: 'CANCEL',
    disableText:
      'Are you sure you want to disable this account?',
    enable: 'Enable Account',
    enableText: 'Are you sure you want to enable this account?',
    resetPassword: 'Reset Password',
    resetPasswordImage: 'resetpasswordimage.svg'
  };
  public static readonly ACCOUNT_PAYLOAD = {
    id: 1,
    active: 0,
    search: '',
    page: 1,
    count: 25,
    sortField: '',
    sortDir: 0,
    searchField: '',
    isExactMatch: false
  };
  public static readonly ACCOUNT_PAYLOAD_FULL = {
    id: 1,
    active: 0,
    search: '',
    page: 1,
    sortField: '',
    sortDir: 0,
    searchField: '',
    isExactMatch: false
  };
  public static readonly ACCOUNTS_HEADER = {
    FIXED: [
      {
        name: 'Account Name',
        value: 'accountName',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        width: '500px'
      }
    ],
    MOVABLE: [
      {
        name: 'Account Status',
        value: 'accountStatus',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: true,
        isOpen: false,
        display: true,
        filterKey: 'accountStatus',
        width: '180px'
      },
      {
        name: 'Start Date',
        value: 'startDate',
        action: true,
        isSort: true,
        isSearch: false,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'startDate',
        width: '170px'
      },
      {
        name: 'End Date',
        value: 'endDate',
        action: true,
        isSort: true,
        isSearch: false,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'endDate',
        width: '170px'
      },
      {
        name: 'Contracted Scholars',
        value: 'contractedScholars',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'contractedScholars',
        width: '170px'
      },
      {
        name: 'Contracted Scholars Used',
        value: 'contractedScholarsUsed',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'contractedScholarsUsed',
        width: '170px'
      },
      {
        name: 'Active Programs',
        value: 'programCount',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        display: false,
        filterKey: 'programCount',
        width: '170px'
      },
      {
        name: 'Invitation Date',
        value: 'scheduleDate',
        action: true,
        isSort: true,
        isSearch: false,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'scheduleDate',
        width: '166px'
      },
      {
        name: 'Primary Account Admin',
        value: 'primaryAccountAdminFullName',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'primaryAccountAdminFullName',
        width: '254px'
      },
      {
        name: 'BX Solution Lead',
        value: 'bxPartnerLeadFullName',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        display: false,
        filterKey: 'bxPartnerLeadFullName',
        width: '250px'
      },
      {
        name: 'BX Success Lead',
        value: 'bxSuccessLeadFullName',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        display: false,
        filterKey: 'bxSuccessLeadFullName',
        width: '250px'
      }
    ]
  };
  public static readonly PROGRAMS_TABLE_HEADER = {
    FIXED: [
      {
        name: 'Program Name',
        value: 'programName',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        width: ' 440px'
      }
    ],
    MOVABLE: [
      {
        name: 'Program Type',
        value: 'programType',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: true,
        isOpen: false,
        display: false,
        filterKey: 'programType',
        width: '210px'
      },
      {
        name: 'Start Date',
        value: 'startDate',
        action: true,
        isSort: true,
        isSearch: false,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'startDate',
        width: '180px'
      },
      {
        name: 'End Date',
        value: 'endDate',
        action: true,
        isSort: true,
        isSearch: false,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'endDate',
        width: '180px'
      },
      {
        name: 'Enrollment Goal',
        value: 'noOfScholar',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        display: false,
        filterKey: 'noOfScholar',
        width: '200px'
      },
      {
        name: 'Enrolled Scholars',
        value: 'enrolledScholars',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'enrolledScholars',
        width: '200px'
      },
      {
        name: 'Status',
        value: 'programStatus',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: true,
        isOpen: false,
        display: true,
        filterKey: 'programStatus',
        width: '180px'
      },
      {
        name: 'Sites/Session',
        value: 'noOfSites',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        display: false,
        filterKey: 'noOfSites',
        width: '196px'
      }
    ]
  };
  public static readonly SITES_TABLE_HEADER = {
    FIXED: [
      {
        name: 'Site/Session',
        value: 'siteName',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        width: '400px'
      }
    ],
    MOVABLE: [
      {
        name: 'Start Date',
        value: 'startDate',
        action: true,
        isSort: true,
        isSearch: false,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'startDate',
        width: '160px'
      },
      {
        name: 'End Date',
        value: 'endDate',
        action: true,
        isSort: true,
        isSearch: false,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'endDate',
        width: '160px'
      },
      {
        name: 'Enrolled Scholars',
        value: 'noOfScholars',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'noOfScholars',
        width: '190px'
      },
      {
        name: 'Capacity',
        value: 'scholarCapacity',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'scholarCapacity',
        width: '140px'
      },
      {
        name: 'Schedule Days',
        value: 'selectedDays',
        action: false,
        isSort: false,
        isSearch: false,
        isSearchList: false,
        isOpen: false,
        display: true,
        filterKey: 'selectedDays',
        width: '190px'
      },
      {
        name: 'Address',
        value: 'siteAddress',
        action: false,
        isSort: false,
        isSearch: false,
        isSearchList: false,
        isOpen: false,
        display: false,
        filterKey: 'siteAddress',
        width: '500px'
      },
      {
        name: 'Time',
        value: 'time',
        action: false,
        isSort: false,
        isSearch: false,
        isSearchList: false,
        isOpen: false,
        display: false,
        filterKey: 'time',
        width: '181px'
      },
      {
        name: 'Age Restriction',
        value: 'ageRestriction',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        display: false,
        filterKey: 'ageRestriction',
        width: '190px'
      },
      {
        name: 'Auto-Enrollment',
        value: 'autoEnrollment',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: true,
        isOpen: false,
        display: false,
        filterKey: 'autoEnrollment',
        width: '185px'
      },
      {
        name: 'Waitlist Capacity',
        value: 'waitListCapacity',
        action: true,
        isSort: true,
        isSearch: true,
        isSearchList: false,
        isOpen: false,
        display: false,
        filterKey: 'waitListCapacity',
        width: '200px'
      }
    ]
  };
  public static NO_ACCOUNT_LIST_AVAILABLE =
    'there is no account, create a new Account';
  public static NO_ACCOUNT_LIST_ON_SEARCH =
    'there is no account with this name, create a new Account';

  public static readonly MANAGE_BX_KEY = [
    'firstName',
    'lastName',
    'email',
    'role',
    'status',
    'action'
  ];
  public static readonly MANAGE_PARTNER_KEY = [
    'firstName',
    'lastName',
    'accountName',
    'role',
    'status',
    'action'
  ];
  public static readonly MANAGE_PROGRAM_KEY = [
    'firstName',
    'lastName',
    'programName',
    'role',
    'status',
    'action'
  ];
  public static readonly MANAGE_USERS_BX_HEADER = [
    { name: 'First Name', value: 'firstName', order: 1 },
    { name: 'Last Name', value: 'lastName', order: 0 },
    { name: 'Email', value: 'email', order: 0 },
    { name: 'Role', value: 'role', order: 0 },
    { name: 'Status', value: 'status', order: 0 }
  ];
  public static readonly MANAGE_USERS_PARTNER_HEADER = [
    { name: 'First Name', value: 'firstName', order: 1 },
    { name: 'Last Name', value: 'lastName', order: 0 },
    { name: 'Account Name', value: 'accountName', order: 0 },
    { name: 'Role', value: 'role', order: 0 },
    { name: 'Status', value: 'status', order: 0 }
  ];
  public static readonly MANAGE_USERS_PROGRAM_HEADER = [
    { name: 'First Name', value: 'firstName', order: 1 },
    { name: 'Last Name', value: 'lastName', order: 0 },
    { name: 'Program Name', value: 'programName', order: 0 },
    { name: 'Role', value: 'role', order: 0 },
    { name: 'Status', value: 'status', order: 0 }
  ];

  public static readonly MANAGE_USERS_SORT_LIST = [
    {
      'First Name': 'First Name',
      type: 'string',
      key: 'firstName'
    },
    {
      'Last Name': 'Last Name',
      type: 'string',
      key: 'lastName'
    },
    { Email: 'Email', type: 'string', key: 'email' },
    { Role: 'Role', type: 'string', key: 'role' },
    { Status: 'Status', type: 'string', key: 'status' },
    { Actions: 'Actions', type: 'string', key: 'action' }
  ];
  public static readonly MANAGE_PAYLOAD = {
    search: '',
    roleType: 1,
    page: 1,
    count: 14,
    sortField: 'firstName',
    sortDir: 0,
    id: 0,
    ProgramId: 0,
    isExactMatch: false
  };
  public static readonly MANAGE_PROGRAM_PAYLOAD = {
    search: '',
    roleType: 2,
    page: 1,
    count: 14,
    sortField: 'firtName',
    sortDir: 0,
    id: 0,
    ProgramId: 0
  };
  public static readonly USERS_PAYLOAD = {
    SearchField: '',
    GlobalSearch: '',
    Active: 0,
    Id: 0,
    Search: '',
    RoleType: 1,
    Page: 1,
    Count: 14,
    SortField: '',
    SortDir: 0,
    isExactMatch: false
  };
  public static readonly MANAGE_USERS_PAYLOAD = {
    SearchField: '',
    GlobalSearch: '',
    Active: 0,
    Id: 0,
    Search: '',
    RoleType: 1,
    Page: 1,
    Count: 14,
    SortField: '',
    SortDir: 0,
    isExactMatch: false
  };
  public static readonly NO_MANAGE_USER_FOUND =
    'No User found for the provided search parameter!';
  public static readonly NO_USER_LIST_FOUND =
    'No User list found, create a new User';
  public static readonly STATUS_MAPPINGS = [
    { Status: 'Active', count: 1, color: 'green' },
    { Status: 'Inactive', count: 2, color: 'grey' },
    { Status: 'Deleted', count: 3, color: 'red' },
    { Status: 'InvitePending', count: 4, color: 'pinkish' },
    { Status: 'InviteExpired', count: 5, color: 'black' }
  ];
  public static readonly MANAGE_USERS = '/users';
  public static readonly UNAUTHORIZED_USER = 'unauthorized';

  public static readonly ENROLLMENT_MANAGER_HEADER = [
    { name: 'Waitlist Number', value: 'waitlistNumber' },
    { name: '', value: 'checkBox' },
    { name: 'Scholar Name', value: 'scholarName', order: 0 },
    { name: 'Age', value: 'age', order: 0 },
    { name: 'Submitted On', value: 'createdOn', order: 2 },
    {
      name: 'Application Status',
      value: 'applicationStatus',
      order: 0
    },
    { name: 'Notes', value: 'notes' }
  ];

  public static readonly MANAGE_APPLICATIONS_HEADER = [
    { name: 'Application Name', value: 'name', order: 1 },
    { name: 'Program Name', value: 'programName', order: 0 },
    { name: 'Application Status', value: 'status', order: 0 },
    { name: 'Last Modified', value: 'modifiedAt', order: 0 },
    {
      name: 'Program Start',
      value: 'programStartDate',
      order: 0
    }
  ];

  public static readonly MANAGE_FORMS_HEADER = [
    {
      name: 'Form Name',
      value: 'formName',
      order: 0,
      isMobile: true
    },
    { name: 'Notes', value: 'notes', isMobile: true },
    {
      name: 'Created On',
      value: 'createdOn',
      order: 0,
      isMobile: false
    },
    {
      name: 'Last Modified',
      value: 'lastModifiedDate',
      order: 0,
      isMobile: false
    }
  ];
  public static readonly PROGRAM_SORT_LIST = [
    {
      'Program Name': 'Program Name',
      type: 'string',
      key: 'programName'
    },
    {
      'Start Date': 'Start Date',
      type: 'string',
      key: 'startDate'
    },
    { 'End Date': 'End Date', type: 'string', key: 'endDate' },
    {
      'No. of Scholars': 'No. of Scholars',
      type: 'string',
      key: 'noOfScholar'
    },
    {
      'No. of Site/Session': 'No. of Site/Session',
      type: 'string',
      key: 'noOfSites'
    },
    { Status: 'Status', type: 'string', key: 'status' }
  ];
  public static readonly PROGRAM_KEY = [
    'programName',
    'startDate',
    'endDate',
    'noOfScholar',
    'noOfSite/Session',
    'status'
  ];
  public static readonly PROGRAM_ACTION = {
    action: 'program',
    edit: 'Edit Program',
    disable: 'Disable Program',
    cancel: 'CANCEL',
    disableText:
      'Are you sure you want to disable this program?',
    enable: 'Enable Program',
    enableText: 'Are you sure you want to enable this program?',
    active: '1',
    inactive: '2',
    resetProgram: 'Reset Program',
    resetText: 'Are you sure you want to reset this program?'
  };
  public static readonly PROGRAM_PAYLOAD = {
    id: 0,
    active: 0,
    search: '',
    page: 1,
    count: 25,
    sortField: '',
    sortDir: 0,
    searchField: '',
    isExactMatch: false
  };
  public static readonly PROGRAM_PAYLOAD_FULL = {
    id: 0,
    active: 0,
    search: '',
    page: 1,
    sortField: '',
    sortDir: 0,
    searchField: '',
    isExactMatch: false
  };
  public static readonly SITES_PAYLOAD = {
    id: 0,
    active: 0,
    searchField: '',
    search: '',
    status: 1,
    page: 1,
    count: 25,
    sortField: '',
    sortDir: 0,
    isExactMatch: false
  };
  public static readonly PROGRAMS_HEADER = [
    { name: 'Program Name', value: 'programName', order: 1 },
    { name: 'Start Date', value: 'startDate', order: 0 },
    { name: 'End Date', value: 'endDate', order: 0 },
    { name: 'No. of Scholars', value: 'noOfScholar', order: 0 },
    {
      name: 'No. of Site/Session',
      value: 'noOfSites',
      order: 0
    },
    { name: 'Status', value: 'status', order: 0 },
    { name: 'Actions' }
  ];
  public static readonly PROGRAMID = 'programsId';
  public static readonly ACCOUNTID = 'accountId';

  public static readonly SITE_HEADER = [
    { name: 'Site/Session Name', value: 'siteName', order: 1 },
    { name: 'Site/Session Start Date', value: 'startDate' },
    { name: 'Site/Session End Date', value: 'endDate' },
    { name: 'Site/Session Start Time', value: 'startTime' },
    { name: 'Site/Session End Time', value: 'endTime' },
    { name: 'Site/Session Address', value: 'siteFullAddress' },
    { name: 'Scholar Capacity', value: 'scholarCapacity' },
    { name: 'Enrolled Scholars', value: 'noOfScholars' },
    { name: 'Age Restriction', value: 'ageRestriction' },
    { name: 'Age as of', value: 'ageAsOf' },
    { name: 'Scheduled Days', value: 'selectedDays' },
    { name: 'Auto-enrollment', value: 'autoEnroll' },
    { name: 'Waitlist Capacity', value: 'waitListCapacity' },
    { name: 'Remove' }
  ];
  public static readonly SITE_SORT_LIST = [
    { number: 'number', key: 'number' },
    { pencil_icon: 'pencil_icon', key: 'pencil_icon' },
    {
      'Site/Session Name': 'Site/Session Name',
      type: 'string',
      key: 'siteName'
    },
    {
      'Site/Session Start Date': 'Site/Session Start Date',
      type: 'string',
      key: 'startDate'
    },
    {
      'Site/Session End Date': 'Site/Session End Date',
      type: 'string',
      key: 'endDate'
    },
    {
      'Enrolled Scholars': 'Enrolled Scholars',
      type: 'string',
      key: 'noOfScholars'
    },
    {
      'Site/Session Address': 'Site/Session Address',
      type: 'string',
      key: 'siteAddress'
    },
    {
      'Address Line 2': 'Address Line 2',
      type: 'string',
      key: 'siteAddress2'
    },
    { City: 'City', type: 'string', key: 'city' },
    { state: 'state', type: 'string', key: 'state' },
    { Zipcode: 'Zipcode', type: 'string', key: 'postalCode' },
    { Remove: 'Remove', type: 'string', key: 'remove' }
  ];
  public static readonly SITE_KEY = [
    'siteName',
    'startDate',
    'endDate',
    'startTime',
    'endTime',
    'siteFullAddress',
    'scholarCapacity',
    'noOfScholars',
    'ageRestriction',
    'ageAsOf',
    'selectedDays',
    'autoEnroll',
    'waitListCapacity',
    'remove'
  ];
  public static readonly STAFF_SORT_LIST = [
    {
      'Last Name': 'Last Name',
      type: 'string',
      key: 'lastName'
    },
    { name: 'Last Login', value: 'lastLogin' },
    { name: 'Email', value: 'email' },
    { name: 'Phone Number', value: 'phoneNumber' }
  ];
  public static readonly STAFFS_HEADER = [
    {
      name: 'Last Name',
      order: 0,
      required: 1,
      value: 'lastName'
    },
    {
      name: 'First Name',
      order: 0,
      required: 1,
      value: 'firstName'
    },
    { name: 'Site/Session Name', value: 'siteName', order: 0 },
    { name: 'System Role', value: 'systemRole', order: 0 },
    { name: 'Program Role', order: 0, value: 'programRole' },
    { name: 'Group', order: 0, value: 'group' },
    { name: 'Invite Status', order: 0, value: 'status' },
    { name: 'Last Login', value: 'lastLogin' },
    { name: 'Email', value: 'email' },
    { name: 'Phone Number', value: 'phoneNumber' },
    { name: 'Remove', value: 'remove' }
  ];
  public static readonly STAFF_KEY = [
    'lastName',
    'firstName',
    'siteName',
    'systemRole',
    'programRole',
    'group',
    'status',
    'lastLogin',
    'email',
    'phoneNumber',
    'remove'
  ];
  public static readonly STAFF_PAYLOAD = {
    ProgramId: 0,
    id: 0,
    active: 0,
    search: '',
    roleType: 0,
    page: 1,
    count: 14
  };
  public static readonly SITE_PAYLOAD = {
    ProgramId: 0,
    id: 0,
    active: 0,
    search: '',
    roleType: 0,
    page: 1,
    count: 14
  };
  public static readonly INVITATION_STATUS = [
    'Accepted',
    'Inactive',
    'Deleted',
    'Pending',
    'Expired'
  ];

  public static readonly ENROLLMENT_SCHOLAR_PAYLOAD = {
    programId: 0,
    status: 2,
    search: '',
    sortField: 'createdOn',
    sortDir: 2,
    page: 1,
    count: 14
  };
  public static readonly ENROLLED_SCHOLARS = {
    pendingScholarCount: 0,
    waitlistScholarCount: 0,
    deniedScholarCount: 0
  };
  public static readonly ENROLL_ADD_EDIT_APPLICATION_PAYLOAD = {
    id: '',
    name: '',
    programId: 0,
    programName: '',
    accountId: 0,
    status: 0,
    timeStamp: '',
    directions: '',
    createdAt: '',
    createdBy: 0,
    modifiedAt: '',
    modifiedBy: 0,
    parts: [
      {
        partSequence: 0,
        openInBuilder: false,
        forms: []
      }
    ]
  };

  public static readonly APPLICATIONS_PAYLOAD = {
    accountId: 0,
    programId: 0,
    status: 0,
    search: '',
    sortField: 'name',
    sortDir: 1,
    page: 1,
    count: 14
  };

  public static readonly PAGE = 'page';
  public static readonly PAYLOAD = 'payload';
  public static readonly OPEN_STATUS = 'openStatus';
  public static readonly DELETE_POPUP = 'deletePopup';
  public static readonly CITE_OBJECTS = 'citeObjects';

  public static readonly STRING: string = 'string';
  public static readonly EMAIL: string = 'email';
  public static readonly NUMBER: string = 'number';
  public static readonly PHONE_NUMBER: string = 'phoneNumber';
  public static readonly PHONE_TYPE: string = 'phoneType';
  public static readonly NO_OF_SCHOLARS: string =
    'numberOfScholars';
  public static readonly GET_CLASS_ROOM: string = 'classrooms';

  //ADD SITE
  public static readonly INVALID_POSTAL_ERROR_TEXT =
    'postal code should consist of 5 integers';
  public static readonly ADD_SITE_RESPONSE_ERROR_TEXT =
    'Some Error Occured';
  public static readonly TEXT_AREA = 'textArea';
  public static readonly TEXT_VALUE = 'textValue';
  public static readonly PROGRAM_KEYS_EXCLUDE = [
    'programName',
    'programStartDate',
    'programEndDate',
    'numberScholars'
  ];
  public static readonly MANAGE_KEYS_EXCLUDE = [
    'firstName',
    'lastName',
    'email',
    'phoneNumber',
    'textAreaInvite'
  ];
  public static readonly SITE_KEYS_EXCLUDE = [
    'siteName',
    'siteAddress',
    'siteAddress2',
    'city',
    'state',
    'postalCode',
    'siteStartDate',
    'siteEndDate',
    'selectedDays'
  ];
  public static readonly CITIES_STRING = 'cities';
  public static readonly STATES = 'states';
  public static readonly TRASH_HOLDER = 'trash-holder';
  public static readonly AREA_INVITE = 'textAreaInvite';
  public static readonly ADDRESS_2 = 'siteAddress2';
  public static readonly POPUP_TEXT = 'popup-text';
  public static readonly DELETE_CONTAINER =
    'delete-popup-container';
  public static readonly COUNTRY_NAME = 'US';
  public static readonly COUNTRY_ID = 233;
  public static readonly REMOVED_STATES = [
    'UM-81',
    'UM-84',
    'UM-86',
    'UM-67',
    'UM-89',
    'UM-71',
    'UM-76',
    'UM-95',
    'UM',
    'UM-79',
    'AS',
    'GU',
    'MP',
    'PR',
    'VI'
  ];
  public static readonly PROFILE_ACCORDION_TILES = [
    {
      textValue: 'Confirm Program Information',
      openStatus: true,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      programName: {
        value: '',
        errorStatus: ''
      },
      programStartDate: {
        value: '',
        errorStatus: '',
        startDateErr: false
      },
      programEndDate: {
        value: '',
        errorStatus: '',
        endDateErr: false
      },
      numberScholars: {
        value: '',
        errorStatus: ''
      }
    },
    {
      textValue: 'Add Site/Session Information',
      openStatus: false,
      textArea:
        'Even if you don’t have all your site/session information right now, please enter placeholder text. You’ll be able to edit this section later.',
      sitePanels: []
    },
    {
      textValue: 'Add Session Managers',
      openStatus: false,
      textArea:
        'Session Managers have access to scholar rosters, attendance, event planning, and tasks for their site/session. They can also invite team members to use BellXcel.',
      sessionManagers: []
    }
  ];
  public static siteObj = {
    openStatus: false,
    deletePopup: false,
    siteName: {
      value: '',
      errorStatus: ''
    },
    siteAddress: {
      value: '',
      errorStatus: ''
    },
    siteAddress2: {
      value: '',
      errorStatus: ''
    },
    city: {
      value: '',
      errorStatus: ''
    },
    state: {
      value: '',
      errorStatus: ''
    },
    postalCode: {
      value: '',
      errorStatus: '',
      zipcodeError: false,
      invalidZipCode: false,
      invalidZipError: 'Invalid Zipcode'
    },
    siteStartDate: {
      value: '',
      errorStatus: '',
      siteStartDateErr: false
    },
    siteEndDate: {
      value: '',
      errorStatus: '',
      siteEndDateErr: false
    },
    selectedDays: {
      value: '',
      errorStatus: ''
    }
  };

  public static readonly LEGACY_ATTENDANCE_HEADER_DESKTOP = {
    FIXED: [
      {
        name: 'First Name',
        value: 'firstName',
        action: true,
        isOpen: false,
        order: 1
      },
      {
        name: 'Last Name',
        value: 'lastName',
        action: true,
        isOpen: false,
        order: 0
      }
    ],
    CHECKIN: [
      {
        name: 'Transportation',
        value: 'arrivalPlan',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Check-in',
        value: 'check',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Time',
        value: 'time',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Note',
        value: 'note',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      }
    ],
    CHECKOUT: [
      {
        name: 'Transportation',
        value: 'dismissalPlan',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Check-out',
        value: 'check',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Time',
        value: 'time',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Note',
        value: 'note',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      }
    ],
    WEEKS: [
      {
        name: 'Transportation',
        value: 'transportation',
        action: true,
        isOpen: false,
        order: 0
      },
      {
        name: 'Check-out',
        value: 'dismissalPlan',
        action: true,
        isOpen: false,
        order: 0
      },
      {
        name: 'Time',
        value: 'time',
        action: true,
        isOpen: false,
        order: 0
      },
      {
        name: 'Note',
        value: 'note',
        action: true,
        isOpen: false,
        order: 0
      }
    ]
  };

  public static readonly ATTENDANCE_HEADER_DESKTOP = {
    FIXED: [
      {
        name: 'First name',
        value: 'firstName',
        action: true,
        isOpen: false,
        order: 1
      },
      {
        name: 'Last name',
        value: 'lastName',
        action: true,
        isOpen: false,
        order: 0
      }
    ],
    CHECKIN: [
      {
        name: 'Grade',
        value: 'grade',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Transportation',
        value: 'arrivalPlan',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Check-in',
        value: 'check',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Time',
        value: 'time',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Note',
        value: 'note',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      }
    ],
    CHECKOUT: [
      {
        name: 'Grade',
        value: 'grade',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Transportation',
        value: 'dismissalPlan',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Check-out',
        value: 'check',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Time',
        value: 'time',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Note',
        value: 'note',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      }
    ],
    WEEKS: [
      {
        name: 'Grade',
        value: 'grade',
        action: true,
        isOpen: false,
        order: 0,
        display: true
      },
      {
        name: 'Transportation',
        value: 'transportation',
        action: true,
        isOpen: false,
        order: 0
      },
      {
        name: 'Check-out',
        value: 'dismissalPlan',
        action: true,
        isOpen: false,
        order: 0
      },
      {
        name: 'Time',
        value: 'time',
        action: true,
        isOpen: false,
        order: 0
      },
      {
        name: 'Note',
        value: 'note',
        action: true,
        isOpen: false,
        order: 0
      }
    ]
  };

  public static readonly GET_DAYS = [
    { initial: 'Su', label: 'Sunday' },
    { initial: 'M', label: 'Monday' },
    { initial: 'T', label: 'Tuesday' },
    { initial: 'W', label: 'Wednesday' },
    { initial: 'Th', label: 'Thursday' },
    { initial: 'F', label: 'Friday' },
    { initial: 'S', label: 'Saturday' }
  ];

  public static readonly CITIES = [
    'New York',
    'Los Angeles',
    'Chicago',
    'San Jose'
  ];
  public static sessionManagerObj = {
    openStatus: false,
    deletePopup: false,
    citeObjects: [
      {
        openStatus: false,
        deletePopup: false,
        siteName: {
          value: '',
          errorStatus: ''
        },
        siteAddress: {
          value: '',
          errorStatus: ''
        },
        siteAddress2: {
          value: '',
          errorStatus: ''
        },
        city: {
          value: '',
          errorStatus: ''
        },
        state: {
          value: '',
          errorStatus: ''
        },
        postalCode: {
          value: '',
          errorStatus: ''
        }
      }
    ],
    firstName: {
      value: '',
      errorStatus: '',
      fNameError: false,
      fNameAlphaCheck: true,
      fNameMinimumLengthCheck: false
    },
    lastName: {
      value: '',
      errorStatus: '',
      lNameError: false,
      lNameAlphaCheck: true,
      fNameMinimumLengthCheck: false
    },
    email: {
      value: '',
      errorStatus: '',
      emailError: ''
    },
    phoneNumber: {
      value: '',
      errorStatus: '',
      phoneError: ''
    },
    textAreaInvite: {
      value: '',
      errorStatus: ''
    }
  };

  public static readonly PROGRAM_SAVE_KEYS = [
    'name',
    'startDate',
    'endDate',
    'numberOfScholars'
  ];
  public static readonly SITES_SAVE_KEYS = [];
  public static readonly MANAGERS_SAVE_KEYS = [];
  public static readonly SAVE_BUTTON_CONFIGURE =
    'saveButtonClick';
  public static readonly ALERT_END_DATE =
    'End date should be greater than Start date';
  public static readonly FILL_START =
    'Please fill in the start date first';
  public static readonly END_DATE = 'endDate';
  public static readonly START_DATE_ID = 'StartDate';
  public static readonly END_DATE_ID = 'EndDate';
  public static readonly TASK_SHOW_TABS = [
    { buttonType: 'Session Manager List', buttonStatus: true },
    { buttonType: 'My List', buttonStatus: false },
    { buttonType: 'Full List', buttonStatus: false }
  ];
  public static readonly TASKS_LIST = [
    {
      id: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
      programId: 0,
      productId: programConfigure.productId,
      cmsTaskId: 0,
      title: 'Establish Program Goals',
      description:
        'Before your new program starts, reflect on previous programs to establish program goals for this year',
      startDate: '2020-02-27T12:59:36.654',
      endDate: '2020-02-27T12:59:36.654Z',
      assignedTo: 'ProgramAdmin',
      isRemoved: true,
      status: 0,
      createdBy: 0,
      createdAt: '2020-02-27T12:59:36.654Z',
      modifiedBy: 0,
      modifiedAt: '2020-02-27T12:59:36.654Z'
    },
    {
      id: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
      programId: 0,
      productId: programConfigure.productId,
      cmsTaskId: 0,
      title: 'Establish new type of Program Goals 1233334567',
      description:
        'Before your new program starts, reflect on previous programs to establish program goals for this year',
      startDate: '2020-01-27T12:59:36.654',
      endDate: '2020-02-27T12:59:36.654Z',
      assignedTo: 'SessionAdmin',
      isRemoved: true,
      status: 0,
      createdBy: 0,
      createdAt: '2020-02-27T12:59:36.654Z',
      modifiedBy: 0,
      modifiedAt: '2020-02-27T12:59:36.654Z'
    },
    {
      id: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
      programId: 0,
      productId: programConfigure.productId,
      cmsTaskId: 0,
      title: 'Establish Program 123333',
      description:
        'Before your new program starts438t3c4c3y4y384t38444444, reflect on previous programs to establish program goals for this year',
      startDate: '2020-01-27T12:59:36.654',
      endDate: '2020-02-27T12:59:36.654Z',
      assignedTo: 'SessionAdmin',
      isRemoved: true,
      status: 0,
      createdBy: 0,
      createdAt: '2020-02-27T12:59:36.654Z',
      modifiedBy: 0,
      modifiedAt: '2020-02-27T12:59:36.654Z'
    },
    {
      id: '3fa85f64-5717-4562-b3fc-2c963f66afa7',
      programId: 0,
      productId: programConfigure.productId,
      cmsTaskId: 0,
      title: 'Create your professional learning plan',
      description:
        'Before your new program starts, reflect on previous programs to establish program goals for this year',
      startDate: '2020-02-27T12:59:36.654Z',
      endDate: '2020-02-27T12:59:36.654Z',
      assignedTo: 'SessionAdmin',
      isRemoved: true,
      status: 0,
      createdBy: 0,
      createdAt: '2020-02-27T12:59:36.654Z',
      modifiedBy: 0,
      modifiedAt: '2020-02-27T12:59:36.654Z'
    },
    {
      id: '3fa85f64-5717-4562-b3fc-2c963f66afa8',
      programId: 0,
      productId: programConfigure.productId,
      cmsTaskId: 0,
      title: 'Create and launch a Staffing Recruitment Plan',
      description:
        'Before your new program starts, reflect on previous programs to establish program goals for this year',
      startDate: '2020-03-27T12:59:36.654Z',
      endDate: '2020-02-27T12:59:36.654Z',
      assignedTo: 'ProgramAdmin',
      isRemoved: true,
      status: 0,
      createdBy: 0,
      createdAt: '2020-02-27T12:59:36.654Z',
      modifiedBy: 0,
      modifiedAt: '2020-02-27T12:59:36.654Z'
    },
    {
      id: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
      programId: 0,
      productId: programConfigure.productId,
      cmsTaskId: 0,
      title: 'Schedule intro meeting with Principal',
      description:
        'Before your new program starts, reflect on previous programs to establish program goals for this year',
      startDate: '2020-03-27T12:59:36.654Z',
      endDate: '2020-02-27T12:59:36.654Z',
      assignedTo: 'SessionAdmin',
      isRemoved: true,
      status: 0,
      createdBy: 0,
      createdAt: '2020-02-27T12:59:36.654Z',
      modifiedBy: 0,
      modifiedAt: '2020-02-27T12:59:36.654Z'
    }
  ];
  public static readonly GET_MONTHS = [
    { index: 0, monthName: 'January', openStatus: false },
    { index: 1, monthName: 'February', openStatus: false },
    { index: 2, monthName: 'March', openStatus: false },
    { index: 3, monthName: 'April', openStatus: false },
    { index: 4, monthName: 'May', openStatus: false },
    { index: 5, monthName: 'June', openStatus: false },
    { index: 6, monthName: 'July', openStatus: false },
    { index: 7, monthName: 'August', openStatus: false },
    { index: 8, monthName: 'September', openStatus: false },
    { index: 9, monthName: 'October', openStatus: false },
    { index: 10, monthName: 'November', openStatus: false },
    { index: 11, monthName: 'December', openStatus: false }
  ];

  public static readonly ROLES = {
    BX_SUPER_ADMIN: 1,
    BX_ADMIN: 2,
    BX_CONTENT_ADMIN: 3,
    ACCOUNT_ADMIN: 4,
    PROGRAM_ADMIN: 5,
    SESSION_ADMIN: 6,
    STAFF: 7,
    SCHOLAR: 8,
    NO_ACCESS: 9,
    GUARDIAN: 10,
    SYSTEM: 11
  };

  public static readonly ACCOUNT_ADMIN_ROLE_ID = 4;
  public static readonly PROGRAM_ADMIN_ROLE_ID = 5;

  public static readonly PROGRAM_KEYS = [
    'name',
    'startDate',
    'endDate',
    'numberOfScholars'
  ];
  public static readonly SITE_KEYS = [
    'siteName',
    'siteAddress',
    'siteAddress2',
    'city',
    'state',
    'postalCode'
  ];
  public static readonly SESSION_MANAGER_KEYS = [
    'siteName',
    'firstName',
    'lastName',
    'email'
  ];
  public static readonly TASKS_STRUCTURE = {
    January: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 0
    },
    February: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 1
    },
    March: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 2
    },
    April: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 3
    },
    May: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 4
    },
    June: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 5
    },
    July: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 6
    },
    August: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 7
    },
    September: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 8
    },
    October: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 9
    },
    November: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 10
    },
    December: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 11
    }
  };
  public static readonly FILTER_TASKS_STRUCTURE = {
    January: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 0,
      startMonth: 'January',
      year: 2020,
      active: [],
      done: [],
      totalTask: []
    },
    February: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 1,
      startMonth: 'February',
      year: 2020,
      active: [],
      done: [],
      totalTask: []
    },
    March: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 2,
      startMonth: 'March',
      year: 2020,
      active: [],
      done: [],
      totalTask: []
    },
    April: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 3,
      startMonth: 'April',
      year: 2020,
      active: [],
      done: [],
      totalTask: []
    },
    May: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 4,
      startMonth: 'May',
      year: 2020,
      active: [],
      done: [],
      totalTask: []
    },
    June: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 5,
      startMonth: 'June',
      year: 2020,
      active: [],
      done: [],
      totalTask: []
    },
    July: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 6,
      startMonth: 'July',
      year: 2020,
      active: [],
      done: [],
      totalTask: []
    },
    August: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 7,
      startMonth: 'August',
      year: 2020,
      active: [],
      done: [],
      totalTask: []
    },
    September: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 8,
      startMonth: 'September',
      year: 2020,
      active: [],
      done: [],
      totalTask: []
    },
    October: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 9,
      startMonth: 'October',
      year: 2020,
      active: [],
      done: [],
      totalTask: []
    },
    November: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 10,
      startMonth: 'November',
      year: 2020,
      active: [],
      done: [],
      totalTask: []
    },
    December: {
      task: [],
      startDate: [],
      month: '',
      currentDate: '',
      currentDateCount: 0,
      totalCount: 0,
      monthIndex: 11,
      startMonth: 'December',
      year: 2020,
      active: [],
      done: [],
      totalTask: []
    }
  };

  public static readonly DONE_STATUS = 'Done';
  public static readonly ACTIVE_STATUS = 'Active';
  public static readonly MONTH_NAME = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  public static PROGRAM_PLANNER = {
    id: 0,
    productId: programConfigure.productId,
    programId: 0,
    programStartDate: '',
    programEndDate: '',
    productProgramId: 1,
    answerId: 0,
    cmsTaskId: 0,
    title: '',
    startDate: '',
    endDate: '',
    description: '',
    isRemoved: false,
    assignedTo: '',
    status: {
      id: 1,
      state: 'Active'
    },
    createdBy: 0,
    createdAt: ''
  };
  public static VALIDATE_PROG_PLAN_FORM = {
    title: {
      name: 'title',
      error: false,
      errorText: 'Name could not be empty'
    },
    startDate: {
      name: 'startDate',
      error: false,
      errorText: 'Start date could not be empty'
    },
    endDate: {
      name: 'endDate',
      error: false,
      errorText: 'End date could not be empty'
    }
  };

  public static VALIDATE_PROG_PLAN_EVENT = {
    title: {
      name: 'title',
      error: false,
      errorText: 'Name is required'
    },
    startDate: {
      name: 'startDate',
      error: false,
      errorText: 'Start date is required'
    }
  };

  public static PROGRAM_EVENT = {
    id: 0,
    programId: 0,
    title: '',
    name: '',
    location: '',
    startDate: '',
    endDate: '',
    description: '',
    status: {
      id: 1,
      state: 'Active'
    },
    createdBy: 0,
    createdAt: ''
  };

  public static readonly ALERT_PROGRAM_START_DATE =
    'Task start date should be in between program start and end date.';
  public static readonly ALERT_PROGRAM_END_DATE =
    'Task end date should be in between program start and end date.';
  public static readonly FILTER = [
    { month: '', active: 'Active', done: '', year: '' }
  ];
  public static readonly ACTIVE = 'Active';
  public static readonly INACTIVE = 'Inactive';
  public static readonly DELETED = 'Deleted';
  public static readonly INVITE_PENDING = 'InvitePending';
  public static readonly INVITE_EXPIRED = 'InviteExpired';

  public static readonly DONE = 'Done';
  public static readonly FILTER_DATA = 'filterData';
  public static readonly FILTER_LIST = 'filteredList';
  public static readonly SCROLL_EVENT = 'scrollEvent';

  public static readonly SKIPPED_LIST = 'skippedList';
  public static readonly SKIPPED_LIST_COUNT = 'skippedListCount';
  public static readonly GET_USER_DT = 'getUserDetail';
  public static readonly OPEN_POPUP = 'openPopup';
  public static readonly PROGRAM_DATA = {
    programAdminId: 0,
    isFinalSubmit: false,
    programDto: {
      id: 232,
      roleId: 0,
      accId: 110,
      name: 'RaghvendraTestProgram',
      isDummy: false,
      startDate: '01/01/20 05:10',
      endDate: '12/31/20 05:10',
      numberOfScholars: 40,
      status: 1,
      noOfSites: 0
    },
    sitePanels: [],
    sessionManagers: [],
    questions: []
  };
  public static readonly UPDATE_TASK = 'updateTask';
  public static readonly OPEN_CLOSE_FILTER = 'openCloseFilter';
  public static readonly COUNT = 'totalCount';
  public static readonly CLOSE = 'close';
  public static readonly OPEN_UPDATE_TASK_SCREEN =
    'openUpdateTask';
  public static readonly OPEN_TASK = 'opentask';
  public static readonly BACK_BUTTON = 'backbutton';
  public static readonly OPEN_EVENT = 'openevent';
  public static readonly SESSION_ADMIN_ROLE_ID = 6;
  public static readonly STAFF_ROLE_ID = 7;
  public static readonly OPEN_UPDATE_EVENT_SCREEN =
    'openUpdateEvent';
  public static readonly OPEN_UPDATE_HOLIDAY_SCREEN =
    'openUpdateHoliday';
  public static readonly BACK_TO_ADD = 'backtoadd';
  public static readonly DELETE_EVENT = 'deleteEvent';
  public static readonly DELETE_TASK = 'deleteTask';
  public static readonly SCHOLAR_STEPPER = [
    {
      title: 'Upload CSV',
      component: 'upload-file',
      class: 'active'
    },
    {
      title: 'Match Columns',
      component: 'match-columns',
      class: ''
    },
    {
      title: 'Review & Complete',
      component: 'repair-table',
      class: ''
    }
  ];
  public static readonly ERROR_OBJ = [
    {
      title: 'error_screen',
      component: 'error_screen',
      class: ''
    }
  ];
  public static readonly UPLOAD_FILE = 'upload-file';
  public static readonly SCHOLAR = 'scholar';
  public static readonly ACTIVE_CLASS = 'active';
  public static readonly COMPLETE_CLASS = 'complete';
  public static readonly ERROR_DIRECTIONS = [
    'Click DOWNLOAD.',
    'Review your data and fix any issues. ',
    'Save your updated .CSV file.',
    'Navigate to your SCHOLAR ROSTER and click BULK UPLOAD.',
    'Upload your .CSV file.'
  ];

  public static readonly FAMILY_STATUS = [
    'Accepted',
    'Expired',
    'Pending',
    'Invite'
  ];

  public static readonly ROW_ROASTER = [];
  public static readonly SCHOLAR_COLUMN_ROSTER = [
    {
      columnName: 'firstName',
      columnTitle: 'First Name',
      hasIssue: false,
      tempColumnName: 'First Name',
      columnType: ['Global'],
      isRequired: true,
      isHidden: false,
      order: 1
    },
    {
      columnName: 'lastName',
      columnTitle: 'Last Name',
      hasIssue: false,
      tempColumnName: 'Last Name',
      columnType: ['Global'],
      isRequired: true,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'Birthday',
      columnTitle: 'Birth Date',
      tempColumnName: 'Birthday',
      hasIssue: false,
      columnType: ['Global', 'Scholar Info'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'enteringGrade',
      columnTitle: 'Grade',
      hasIssue: false,
      tempColumnName: 'Entering Grade',
      columnType: ['Global', 'Scholar Info'],
      isRequired: true,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'scheduledDays',
      columnTitle: 'Scheduled Days',
      hasIssue: false,
      tempColumnName: 'Scheduled Days',
      columnType: ['Scheduled Days'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'schoolStudentId',
      columnTitle: 'School Student ID',
      hasIssue: false,
      tempColumnName: 'studentSchoolIds',
      columnType: ['Scholar ID'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'externalScholarId',
      columnTitle: 'Scholar ID',
      hasIssue: false,
      tempColumnName: 'scholarIds',
      columnType: ['Scholar ID'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'documents',
      columnTitle: 'documents',
      tempColumnName: 'documents',
      hasIssue: false,
      columnType: ['Scholar Documentation'],
      isRequired: false,
      isHidden: true,
      order: 0
    },
    {
      columnName: 'bellXcelId',
      columnTitle: 'BellXcel Id',
      hasIssue: false,
      tempColumnName: 'bellXcelId',
      columnType: ['Scholar ID'],
      isRequired: false,
      isHidden: true,
      order: 0
    },
    {
      columnName: 'classroomName',
      columnTitle: 'Group',
      tempColumnName: 'classroom',
      hasIssue: false,
      columnType: ['Scholar Info'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'gender',
      columnTitle: 'Gender',
      tempColumnName: 'gender',
      hasIssue: false,
      columnType: ['Scholar Info'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'ethnicity',
      columnTitle: 'Race / Ethnicity',
      tempColumnName: 'ethnicity',
      hasIssue: false,
      columnType: ['Scholar Info'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'status',
      columnTitle: 'Status',
      hasIssue: false,
      columnType: ['Status'],
      tempColumnName: 'status',
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'enrollmentDate',
      columnTitle: 'Enrollment Date',
      hasIssue: false,
      tempColumnName: 'enrollmentDate',
      columnType: ['Status'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'inactiveDate',
      columnTitle: 'Inactive Date',
      hasIssue: false,
      tempColumnName: 'inactiveDate',
      columnType: ['Status'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: '',
      columnTitle: 'Status Notes',
      hasIssue: false,
      tempColumnName: 'inactiveNotes',
      columnType: ['Status'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'scholarAddress1',
      columnTitle: 'Address 1',
      hasIssue: false,
      tempColumnName: 'address1',
      columnType: ['Address'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'scholarAddress2',
      columnTitle: 'Address 2',
      hasIssue: false,
      tempColumnName: 'address2',
      columnType: ['Address'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'city',
      columnTitle: 'City',
      hasIssue: false,
      columnType: ['Address'],
      tempColumnName: 'city',
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'state',
      columnTitle: 'State',
      hasIssue: false,
      tempColumnName: 'state',
      columnType: ['Address'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'zipCode',
      columnTitle: 'Zip',
      hasIssue: false,
      tempColumnName: 'zip',
      columnType: ['Address'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'homePhone',
      columnTitle: 'Home #',
      hasIssue: false,
      tempColumnName: 'home#',
      columnType: ['Address'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'familyContact#1FirstName',
      columnTitle: 'Family 1: First Name',
      hasIssue: false,
      tempColumnName: 'familyContact#1FirstName',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'familyContact#1LastName',
      columnTitle: 'Family 1: Last Name',
      hasIssue: false,
      tempColumnName: 'familyContact#1LastName',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'familyContact#1Phone',
      columnTitle: 'Family 1: Phone',
      hasIssue: false,
      tempColumnName: 'familyContact#1Phone',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'familyContact#1Email',
      columnTitle: 'Family 1: Email',
      hasIssue: false,
      tempColumnName: 'familyContact#1Email',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'allowedToPickUp',
      columnTitle: 'Family 1: Authorized Pickup',
      hasIssue: false,
      tempColumnName: 'allowedToPickUp',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'relationship',
      columnTitle: 'Family 1: Relationship',
      hasIssue: false,
      tempColumnName: 'relationship',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'familyContact#2FirstName',
      columnTitle: 'Family 2: First Name',
      hasIssue: false,
      tempColumnName: 'familyContact#2FirstName',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'familyContact#2LastName',
      columnTitle: 'Family 2: Last Name',
      hasIssue: false,
      tempColumnName: 'familyContact#2LastName',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'familyContact#2Phone',
      columnTitle: 'Family 2: Phone',
      hasIssue: false,
      tempColumnName: 'familyContact#2Phone',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'familyContact#2Email',
      columnTitle: 'Family 2: Email',
      hasIssue: false,
      tempColumnName: 'familyContact#2Email',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'familyContact#2AllowedToPickUp',
      columnTitle: 'Family 2: Authorized Pickup',
      hasIssue: false,
      tempColumnName: 'familyContact#2AllowedToPickUp',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'familyContact#2Relationship',
      columnTitle: 'Family 2: Relationship',
      hasIssue: false,
      tempColumnName: 'familyContact#2Relationship',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'emergencyContactFirstName',
      columnTitle: 'Emergency 1: First Name',
      hasIssue: false,
      tempColumnName: 'emergencyContactFirstName',
      columnType: ['Emergency'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'emergencyContactLastName',
      columnTitle: 'Emergency 1: Last Name',
      hasIssue: false,
      tempColumnName: 'emergencyContactLastName',
      columnType: ['Emergency'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'emergencyContactPrimaryPhone',
      columnTitle: 'Emergency 1: Primary Phone',
      hasIssue: false,
      tempColumnName: 'emergencyContactPrimaryPhone',
      columnType: ['Emergency'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'emergencyContactSecondaryPhone',
      columnTitle: 'Emergency 1: Secondary Phone',
      hasIssue: false,
      tempColumnName: 'emergencyContactSecondaryPhone',
      columnType: ['Emergency'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'epiPen',
      columnTitle: 'Epi Pen',
      hasIssue: false,
      tempColumnName: 'epiPen',
      columnType: ['Medical'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'AsthmaInhaler',
      columnTitle: 'Inhaler',
      hasIssue: false,
      tempColumnName: 'inhaler',
      columnType: ['Medical'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'allergies',
      columnTitle: 'Allergies',
      hasIssue: false,
      tempColumnName: 'allergies',
      columnType: ['Medical'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'medication',
      columnTitle: 'Medications',
      hasIssue: false,
      tempColumnName: 'medications',
      columnType: ['Medical'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'iepStatus',
      columnTitle: 'IEP Status',
      hasIssue: false,
      tempColumnName: 'iepStatus',
      columnType: ['Learning Status'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'ellStatus',
      columnTitle: 'ELL/LEP Status',
      hasIssue: false,
      tempColumnName: 'ellStatus',
      columnType: ['Learning Status'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'specialEducationStatus',
      columnTitle: 'Special Education Status',
      hasIssue: false,
      tempColumnName: 'specialEducationStatus',
      columnType: ['Learning Status'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'scholarInfoForm',
      columnTitle: 'Scholar Info Consent',
      hasIssue: false,
      tempColumnName: 'scholarInfoCF',
      columnType: ['Consent Forms'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'fieldTripForm',
      columnTitle: 'Field Trip Consent',
      hasIssue: false,
      tempColumnName: 'fieldTripCF',
      columnType: ['Consent Forms'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'medicalForm',
      columnTitle: 'Medical Consent',
      hasIssue: false,
      tempColumnName: 'medicalCF',
      columnType: ['Consent Forms'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'emergencyForm',
      columnTitle: 'Emergency Consent',
      hasIssue: false,
      tempColumnName: 'emergencyCF',
      columnType: ['Consent Forms'],
      isRequired: false,
      isHidden: false,
      order: 0
    },
    {
      columnName: 'photoOptOut',
      columnTitle: 'Photo/Video Opt Out',
      hasIssue: false,
      tempColumnName: 'photoVideoOptOut',
      columnType: ['Consent Forms'],
      isRequired: false,
      isHidden: false,
      order: 0
    }
  ];
  public static readonly STAFF_COLUMN_ROSTER = [
    {
      value: 'firstName',
      columnTitle: 'First Name',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: true,
      isHidden: false,
      order: 0,
      name: 'firstName'
    },
    {
      value: 'lastName',
      columnTitle: 'Last Name',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: true,
      isHidden: false,
      order: 0,
      name: 'lastName'
    },
    {
      value: 'email',
      columnTitle: 'Email',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: false,
      isHidden: false,
      name: 'email'
    },
    {
      value: 'phoneNumber',
      columnTitle: 'Phone',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: false,
      isHidden: false,
      name: 'phoneNumber'
    },
    {
      value: 'phoneType',
      columnTitle: 'Phone Type',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: false,
      isHidden: false,
      order: 0,
      name: 'phoneType'
    },
    {
      value: 'systemRole',
      columnTitle: 'System Role',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: false,
      isHidden: false,
      order: 0,
      name: 'systemRole'
    },
    {
      value: 'position',
      columnTitle: 'Position',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: false,
      isHidden: false,
      order: 0,
      name: 'position'
    }
  ];
  public static DISPLAY_COLUMNS = [
    'Scholar Info',
    'Scheduled Days',
    'Scholar ID',
    'Status',
    'Address',
    'Family',
    'Emergency',
    'Medical',
    'Learning Status',
    'Consent Forms',
    'Custom Fields',
    'All Columns'
  ];
  public static SORTED_COLUMNS = [
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false
  ];
  public static REQUIRED_COLUMNS = [
    'lastName',
    'firstName',
    'birthDate',
    'enteringGrade',
    'email'
  ];
  public static REQUIRED_COLUMNS_Email = [
    'lastName',
    'firstName',
    'email',
    'birthDate',
    'enteringGrade'
  ];
  public static CLASSROOM_OPTIONS = [
    'Open Text Field',
    'Pines',
    'Apples',
    'Apples 1',
    'Apples2',
    'Willows',
    'Spruce'
  ];
  public static CUSTOM_OPTIONS = [
    'Shirt size:S',
    'Shirt size:M',
    'Shirt size:L'
  ];
  public static MAPPED_FIELDS = [
    { 'First Name': 'firstName' },
    { 'Last Name': 'lastName' },
    { Birthday: 'birthDate' },
    { Grade: 'enteringGrade' },
    { 'School Student ID': 'studentSchoolIds' },
    { 'Scholar ID': 'scholarIds' },
    { 'BellXcel ID': 'bellXcelId' },
    { Group: 'classroom' },
    { Gender: 'gender' },
    { 'Race / Ethnicity': 'ethnicity' },
    { Status: 'status' },
    { 'Enrollment Date': 'enrollmentDate' },
    { 'Inactive Date': 'inactiveDate' },
    { 'Address 1': 'address1' },
    { 'Address 2': 'address2' },
    { City: 'city' },
    { State: 'state' },
    { Zip: 'zip' },
    { 'Home Phone': 'home#' },
    { 'Family 1 First Name': 'familyContact#1FirstName' },
    { 'Family 1 Last Name': 'familyContact#1LastName' },
    { 'Family 1 Phone': 'familyContact#1Phone' },
    { 'Family 1 Email': 'familyContact#1Email' },
    { 'Family 1 Authorized Pickup': 'allowedToPickUp' },
    { 'Family 1 Relationship': 'relationship' },
    { 'Family 2 First Name': 'familyContact#2FirstName' },
    { 'Family 2 Last Name': 'familyContact#2LastName' },
    { 'Family 2 Phone': 'familyContact#2Phone' },
    { 'Family 2 Email': 'familyContact#2Email' },
    {
      'Family 2 Authorized Pickup':
        'familyContact#2AllowedToPickUp'
    },
    { 'Family 2 Relationship': 'familyContact#2Relationship' },
    {
      'Emergency Contact First Name': 'emergencyContactFirstName'
    },
    {
      'Emergency Contact Last Name': 'emergencyContactLastName'
    },
    {
      'Emergency Contact Phone (primary)':
        'emergencyContactPrimaryPhone'
    },
    {
      'Emergency Contact Phone (other)':
        'emergencyContactSecondaryPhone'
    },
    { 'Epi Pen': 'epiPen' },
    { 'Asthma Inhaler': 'inhaler' },
    { Allergies: 'allergies' },
    { Medications: 'medications' },
    { 'IEP Status': 'iepStatus' },
    { 'ELL/LEP Status': 'ellStatus' },
    { 'Special Education Status': 'specialEducationStatus' },
    { 'Scholar Information Consent Form': 'scholarInfoCF' },
    { 'Field Trip Consent Form': 'fieldTripCF' },
    { 'Medical Consent Form': 'medicalCF' },
    { 'Emergency Consent Form': 'emergencyCF' },
    { 'Photo/Video Opt Out': 'photoVideoOptOut' }
  ];
  public static MAPPED_FIELD_STAFF = [
    { 'First Name': 'firstName' },
    { 'Last Name': 'lastName' },
    { Email: 'email' },
    { Phone: 'phone' },
    { 'Phone Type': 'phoneType' },
    { Position: 'position' },
    { 'System Role': 'systemRole' }
  ];
  public static DATA_STRUCTURE_FIELDS = [
    'lastName',
    'firstName',
    'birthDate',
    'enteringGrade',
    'studentSchoolIds',
    'scholarIds',
    'bellXcelId',
    'classroom',
    'gender',
    'ethnicity',
    'status',
    'enrollmentDate',
    'inactiveDate',
    'address1',
    'address2',
    'city',
    'state',
    'zip',
    'home#',
    'familyContact#1FirstName',
    'familyContact#1LastName',
    'familyContact#1Phone',
    'familyContact#1Email',
    'allowedToPickUp',
    'relationship',
    'familyContact#2FirstName',
    'familyContact#2LastName',
    'familyContact#2Phone',
    'familyContact#2Email',
    'familyContact#2AllowedToPickUp',
    'familyContact#2Relationship',
    'emergencyContactFirstName',
    'emergencyContactLastName',
    'emergencyContactPrimaryPhone',
    'emergencyContactSecondaryPhone',
    'epiPen',
    'inhaler',
    'allergies',
    'iepStatus',
    'ellStatus',
    'specialEducationStatus',
    'scholarInfoCF',
    'fieldTripCF',
    'medicalCF',
    'emergencyCF',
    'photoVideoOptOut',
    'programName',
    'siteName',
    'siteStartDate',
    'siteEndDate',
    'custom1',
    'custom2',
    'custom3',
    'custom4',
    'custom5',
    'custom6'
  ];
  public static VALIDATE_PROGRAM_NAME = {
    prograName: {
      name: 'programName',
      error: false,
      errorText: 'Program Name is required'
    }
  };
  public static ROW_OBJ_TABLE = {
    firstName: {
      value: '',
      issueType: '',
      columnType: ['Global'],
      isRequired: true,
      isHidden: false
    },
    lastName: {
      value: '',
      issueType: '',
      columnType: ['Global'],
      isRequired: true,
      isHidden: false
    },
    birthDate: {
      value: '',
      issueType: '',
      columnType: ['Global', 'Scholar Info'],
      isRequired: true,
      isHidden: false
    },
    enteringGrade: {
      value: '',
      issueType: '',
      columnType: ['Global', 'Scholar Info'],
      isRequired: true,
      isHidden: false
    },
    scheduledDays: {
      value: '',
      issueType: '',
      columnType: ['Scheduled Days'],
      isRequired: false,
      isHidden: false
    },
    studentSchoolIds: {
      value: '',
      issueType: '',
      columnType: ['Scholar ID'],
      isRequired: false,
      isHidden: false
    },
    scholarIds: {
      value: '',
      issueType: '',
      columnType: ['Scholar ID'],
      isRequired: false,
      isHidden: false
    },
    bellXcelId: {
      value: '',
      issueType: '',
      columnType: ['Scholar ID'],
      isRequired: false,
      isHidden: false
    },
    classroom: {
      value: '',
      issueType: '',
      columnType: ['Scholar Info'],
      isRequired: false,
      isHidden: false
    },
    gender: {
      value: '',
      issueType: '',
      columnType: ['Scholar Info'],
      isRequired: false,
      isHidden: false
    },
    ethnicity: {
      value: '',
      issueType: '',
      columnType: ['Scholar Info'],
      isRequired: false,
      isHidden: false
    },
    status: {
      value: '',
      issueType: '',
      columnType: ['Status'],
      isRequired: false,
      isHidden: false
    },
    documents: {
      value: [],
      issueType: '',
      columnType: ['Scholar Documentation'],
      isRequired: false,
      isHidden: true
    },
    enrollmentDate: {
      value: '',
      issueType: '',
      columnType: ['Status'],
      isRequired: false,
      isHidden: false
    },
    inactiveDate: {
      value: '',
      issueType: '',
      columnType: ['Status'],
      isRequired: false,
      isHidden: false
    },
    inactiveNotes: {
      value: '',
      issueType: '',
      columnType: ['Status'],
      isRequired: false,
      isHidden: false
    },
    address1: {
      value: '',
      issueType: '',
      columnType: ['Address'],
      isRequired: false,
      isHidden: false
    },
    address2: {
      value: '',
      issueType: '',
      columnType: ['Address'],
      isRequired: false,
      isHidden: false
    },
    city: {
      value: '',
      issueType: '',
      columnType: ['Address'],
      isRequired: false,
      isHidden: false
    },
    state: {
      value: '',
      issueType: '',
      columnType: ['Address'],
      isRequired: false,
      isHidden: false
    },
    zip: {
      value: '',
      issueType: '',
      columnType: ['Address'],
      isRequired: false,
      isHidden: false
    },
    'home#': {
      value: '',
      issueType: '',
      columnType: ['Address'],
      isRequired: false,
      isHidden: false
    },
    'familyContact#1FirstName': {
      value: '',
      issueType: '',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false
    },
    'familyContact#1LastName': {
      value: '',
      issueType: '',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false
    },
    'familyContact#1Phone': {
      value: '',
      issueType: '',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false
    },
    'familyContact#1Email': {
      value: '',
      issueType: '',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false
    },
    allowedToPickUp: {
      value: '',
      issueType: '',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false
    },
    relationship: {
      value: '',
      issueType: '',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false
    },
    'familyContact#2FirstName': {
      value: '',
      issueType: '',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false
    },
    'familyContact#2LastName': {
      value: '',
      issueType: '',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false
    },
    'familyContact#2Phone': {
      value: '',
      issueType: '',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false
    },
    'familyContact#2Email': {
      value: '',
      issueType: '',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false
    },
    'familyContact#2AllowedToPickUp': {
      value: '',
      issueType: '',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false
    },
    'familyContact#2Relationship': {
      value: '',
      issueType: '',
      columnType: ['Family'],
      isRequired: false,
      isHidden: false
    },
    emergencyContactFirstName: {
      value: '',
      issueType: '',
      columnType: ['Emergency'],
      isRequired: false,
      isHidden: false
    },
    emergencyContactLastName: {
      value: '',
      issueType: '',
      columnType: ['Emergency'],
      isRequired: false,
      isHidden: false
    },
    emergencyContactPrimaryPhone: {
      value: '',
      issueType: '',
      columnType: ['Emergency'],
      isRequired: false,
      isHidden: false
    },
    emergencyContactSecondaryPhone: {
      value: '',
      issueType: '',
      columnType: ['Emergency'],
      isRequired: false,
      isHidden: false
    },
    epiPen: {
      value: '',
      issueType: '',
      columnType: ['Medical'],
      isRequired: false,
      isHidden: false
    },
    inhaler: {
      value: '',
      issueType: '',
      columnType: ['Medical'],
      isRequired: false,
      isHidden: false
    },
    allergies: {
      value: '',
      issueType: '',
      columnType: ['Medical'],
      isRequired: false,
      isHidden: false
    },
    medications: {
      value: '',
      issueType: '',
      columnType: ['Medical'],
      isRequired: false,
      isHidden: false
    },
    iepStatus: {
      value: '',
      issueType: '',
      columnType: ['Learning Status'],
      isRequired: false,
      isHidden: false
    },
    ellStatus: {
      value: '',
      issueType: '',
      columnType: ['Learning Status'],
      isRequired: false,
      isHidden: false
    },
    specialEducationStatus: {
      value: '',
      issueType: '',
      columnType: ['Learning Status'],
      isRequired: false,
      isHidden: false
    },
    scholarInfoCF: {
      value: '',
      issueType: '',
      columnType: ['Consent Forms'],
      isRequired: false,
      isHidden: false
    },
    fieldTripCF: {
      value: '',
      issueType: '',
      columnType: ['Consent Forms'],
      isRequired: false,
      isHidden: false
    },
    medicalCF: {
      value: '',
      issueType: '',
      columnType: ['Consent Forms'],
      isRequired: false,
      isHidden: false
    },
    emergencyCF: {
      value: '',
      issueType: '',
      columnType: ['Consent Forms'],
      isRequired: false,
      isHidden: false
    },
    photoVideoOptOut: {
      value: '',
      issueType: '',
      columnType: ['Consent Forms'],
      isRequired: false,
      isHidden: false
    }
  };
  public static ROW_OBJ_TABLE_STAFF = {
    firstName: {
      value: '',
      columnTitle: 'First Name',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: true,
      isHidden: false
    },
    lastName: {
      value: '',
      columnTitle: 'Last Name',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: true,
      isHidden: false
    },
    email: {
      value: '',
      columnTitle: 'Email',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: true,
      isHidden: false
    },
    phone: {
      value: '',
      columnTitle: 'Phone',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: false,
      isHidden: false
    },
    phoneType: {
      value: '',
      columnTitle: 'Phone Type',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: false,
      isHidden: false
    },
    systemRole: {
      value: '',
      columnTitle: 'System Role',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: false,
      isHidden: false
    },
    position: {
      value: '',
      columnTitle: 'Position',
      hasIssue: false,
      columnType: ['Global'],
      isRequired: false,
      isHidden: false
    }
  };

  // public static ROW_OBJ_TABLE_STAFF = {
  //     'lastName': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Global'],
  //         isRequired: true,
  //         isHidden: false
  //     },
  //     'firstName': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Global'],
  //         isRequired: true,
  //         isHidden: false
  //     },
  //     'siteName': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Global'],
  //         isRequired: false,
  //         isHidden: false,
  //         id: 0
  //     },
  //     'systemRole': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Global'],
  //         isRequired: false,
  //         isHidden: false,
  //         id: 0
  //     },
  //     'programRole': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Global'],
  //         isRequired: false,
  //         isHidden: false,
  //     },
  //     'group': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Scholar Info'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  //     'inviteStatus': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Scholar Info'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  //     'lastLogin': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Scholar Info'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  //     'email': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Global'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  //     'phoneNumber': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Global'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  //     'emergencyContactFirstName': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Emergency'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  //     'emergencyContactLastName': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Emergency'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  //     'emergencyContactRelationship': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Emergency'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  //     'emergencyContactPhoneNumber': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Emergency'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  //     'emergencyContactEmail': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Emergency'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  //     'customNotes': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Notes'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  //     'allergies': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Medical'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  //     'status': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Status'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  //     'inactiveDate': {
  //         value: '',
  //         issueType: '',
  //         columnType: ['Status'],
  //         isRequired: false,
  //         isHidden: false
  //     },
  // }

  public static COLUMN_OBJ_TABLE = {
    columnName: 'lastName',
    columnTitle: 'Last Name',
    hasIssue: false,
    columnType: ['Global'],
    isRequired: true,
    isHidden: false
  };
  public static readonly MANAGE_USER = 'manageuser';

  public static VALIDATE_CREATE_USER_FORM = {
    firstName: {
      name: 'firstName',
      error: false,
      errorText: 'First Name is required',
      alphaCharError: false,
      errorAlpha:
        'Sorry, please use only alphabetic characters. Hyphens and apostrophes are also allowed.',
      minimumCharError: false,
      errorMinimumLength: 'Maximum 50 characters allowed'
    },
    lastName: {
      name: 'lastName',
      error: false,
      errorText: 'Last Name is required',
      alphaCharError: false,
      errorAlpha:
        'Sorry, please use only alphabetic characters. Hyphens and apostrophes are also allowed.',
      minimumCharError: false,
      errorMinimumLength: 'Maximum 50 characters allowed'
    },
    email: {
      name: 'email',
      error: false,
      errorText: 'Email is required'
    },
    roleId: {
      name: 'roleId',
      error: false,
      required: true,
      type: 'array',
      errorText: 'User Role is required'
    },
    accountId: {
      name: 'accountId',
      error: false,
      required: false,
      type: 'array',
      errorText: 'Account is required'
    },
    programId: {
      name: 'programId',
      error: false,
      required: false,
      type: 'array',
      errorText: 'Program is required'
    },
    siteId: {
      name: 'siteId',
      error: false,
      required: false,
      type: 'array',
      errorText: 'Site/Session is required'
    }
  };

  public static VALIDATE_PARTNER_USER_FORM = {
    firstName: {
      name: 'firstName',
      error: false,
      errorText: 'First Name is required',
      alphaCharError: false,
      errorAlpha:
        'Sorry, please use only alphabetic characters. Hyphens and apostrophes are also allowed.'
    },
    lastName: {
      name: 'lastName',
      error: false,
      errorText: 'Last Name is required',
      alphaCharError: false,
      errorAlpha:
        'Sorry, please use only alphabetic characters. Hyphens and apostrophes are also allowed.'
    },
    accountId: {
      name: 'accountId',
      error: false,
      required: false,
      type: 'array',
      errorText: 'Account is required'
    }
  };

  public static VALIDATE_USER_FORM = {
    firstName: {
      name: 'firstName',
      error: false,
      errorText: 'First Name is required',
      alphaCharError: false,
      errorAlpha:
        'Sorry, please use only alphabetic characters. Hyphens and apostrophes are also allowed.'
    },
    lastName: {
      name: 'lastName',
      error: false,
      errorText: 'Last Name is required',
      alphaCharError: false,
      errorAlpha:
        'Sorry, please use only alphabetic characters. Hyphens and apostrophes are also allowed.'
    }
  };

  public static PARTNER_USER_CREATION = {
    id: 0,
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    personalInvitationNote: '',
    userRoles: [],
    forgetPasswordGuid: '',
    roleName: 'Select System Role',
    accountName: 'Select',
    programName: 'Select Program',
    siteName: 'Select Site/Session'
  };

  public static USERS_CREATION = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    personalInvitationNote: '',
    phoneType: '',
    scheduleDate: '',
    userRoles: []
  };

  public static USERROLE_OBJ = {
    roleId: 0,
    accountId: 0,
    programId: 0,
    siteId: 0,
    programRole: '',
    status: 0,
    isPrimaryAccountAdmin: false
  };

  public static ROLE = {
    roleId: 0,
    accountId: 0,
    programId: 0,
    siteId: 0,
    programRole: '',
    status: 0,
    isPrimaryAccountAdmin: false,
    roleName: '',
    sites: [],
    totalSites: [],
    programs: [],
    position: '',
    otherPosition: '',
    addProgram: [],
    positionDropdownState: false,
    addProgramIsShow: false,
    programButtonShow: false,
    otherPositionShow: false,
    allProgramsSelected: false,
    allSiteSelected: false,
    accordOpen: false,
    isShow: false,
    programError: false,
    siteError: false,
    toggleIndex: 1,
    accordionError: false,
    allMandatoryFieldsFilled: true,
    newPrivilegesAdded: []
  };

  public static ADD_PROGRAM_OBJ = {
    roleId: 0,
    roleName: '',
    sites: [],
    totalSites: [],
    programs: [],
    position: '',
    otherPosition: '',
    positionDropdownState: false,
    otherPositionShow: false,
    allProgramSelected: false,
    allSiteSelected: false,
    programError: false,
    siteError: false,
    toggleIndex: 1,
    accordionError: false,
    allMandatoryFieldsFilled: true,
    newPrivilegesAdded: []
  };

  public static SYSTEM_ROLE = [
    { id: 5, name: 'Program Admin' },
    { id: 6, name: 'Session Admin' },
    { id: 7, name: 'Staff' },
    { id: 9, name: 'No Access' }
  ];

  public static SYSTEM_ROLE_LIST = [
    {
      id: 4,
      name: 'Primary Account Admin',
      isChecked: false,
      disable: false
    },
    {
      id: 4,
      name: 'Account Admin',
      isChecked: false,
      disable: false
    },
    {
      id: 5,
      name: 'Program Admin',
      isChecked: false,
      disable: false
    },
    {
      id: 6,
      name: 'Session Admin',
      isChecked: false,
      disable: false
    },
    { id: 7, name: 'Staff', isChecked: false, disable: false },
    {
      id: 9,
      name: 'No Access',
      isChecked: false,
      disable: false
    }
  ];

  public static SYSTEM_PROGRAM_ROLE_LIST = [
    {
      id: 5,
      name: 'Program Admin',
      isChecked: false,
      disable: false
    },
    {
      id: 6,
      name: 'Session Admin',
      isChecked: false,
      disable: false
    },
    { id: 7, name: 'Staff', isChecked: false, disable: false },
    {
      id: 9,
      name: 'No Access',
      isChecked: false,
      disable: false
    }
  ];

  public static SYSTEM_SITE_ROLE_LIST = [
    {
      id: 6,
      name: 'Session Admin',
      isChecked: false,
      disable: false
    },
    { id: 7, name: 'Staff', isChecked: false, disable: false },
    {
      id: 9,
      name: 'No Access',
      isChecked: false,
      disable: false
    }
  ];

  public static ROLE_WITH_COUNT = [
    {
      id: 4,
      name: 'account admin',
      count: 0
    },
    {
      id: 5,
      name: 'program admin',
      count: 0
    },
    {
      id: 6,
      name: 'session admin',
      count: 0
    },
    {
      id: 7,
      name: 'staff',
      count: 0
    },
    {
      id: 9,
      name: 'no access',
      count: 0
    }
  ];

  public static USER_CREATION = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    status: 1,
    inactiveDate: '',
    personalInvitationNote: '',
    userRoles: [
      {
        id: 0,
        roleId: 0,
        accountId: 0,
        programId: 0,
        siteId: 0,
        isPrimaryAccountAdmin: false
      }
    ],
    forgetPasswordGuid: '',
    roleName: 'Select System Role'
  };
  public static USERS_INVITATION_BODY =
    'The person you invited will receive an email shortly.';

  public static readonly NAV_DECIDER = [
    {
      roleType: 7,
      background: '',
      navView: [
        {
          title: 'Program planner',
          isNested: false,
          route: '/planner',
          image: 'programplannericon.svg',
          class: 'programplannericon',
          isSelected: false,
          dropdownClass: 'purple-theme'
        },
        {
          title: 'Participants',
          isNested: true,
          route: '',
          image: 'scholars.svg',
          class: 'scholars',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'green-list',
          nestedDrop: [
            {
              title: 'Roster',
              isNested: false,
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_ENROLLMENT_REDIRECT_URL}roster`
            },
            {
              title: 'Attendance',
              isNested: false,
              route: '/attendance',
              isSelected: false
            }
          ]
        },
        {
          title: 'Analytics',
          isNested: true,
          route: '',
          image: 'chart.svg',
          class: 'programplannericon',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'analytics-list',
          nestedDrop: [
            {
              title: 'Participant',
              isNested: false,
              isSelected: false,
              route: '/analytics/scholar'
            }
          ]
        },
        {
          title: `${APP_CONST.LEARNING}`,
          image: 'resources.svg',
          class: 'resources',
          dropdownClass: 'dark-blue-list',
          isNested: true,
          route: '',
          openStatus: false,
          isSelected: false,
          nestedDrop: [
            {
              title: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.menuTitle}`,
              isNested: false,
              isSelected: false,
              route: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.path}`
            },
            {
              title: `${APP_CONST.APP_ROUTES.CMS_CATALOG.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.CMS_CATALOG.path}`,
              isSelected: false
            },
            {
              title: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.path}`,
              isSelected: false
            }
          ]
        },
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300px',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'My profile',
              isNested: false,
              route: '',
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_IDENTITY_REDIRECT_URL}user/profile`
            },
            {
              title: 'Help center',
              isNested: false,
              route: '',
              isSelected: false,
              id: 3,
              callback: 'navigateUrl'
            },
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ],
      learningExperienceDirectAccessNavView: [
        {
          title: `${APP_CONST.LEARNING}`,
          image: 'resources.svg',
          class: 'resources',
          dropdownClass: 'dark-blue-list',
          isNested: true,
          route: '',
          openStatus: false,
          isSelected: false,
          nestedDrop: [
            {
              title: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.path}`,
              isSelected: false
            },
            {
              title: `${APP_CONST.APP_ROUTES.CMS_CATALOG.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.CMS_CATALOG.path}`,
              isSelected: false
            },
            {
              title: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.path}`,
              isSelected: false
            }
          ]
        },
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300px',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'My profile',
              isNested: false,
              route: '',
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_IDENTITY_REDIRECT_URL}user/profile`
            },
            {
              title: 'Help center',
              isNested: false,
              route: '',
              id: 3,
              callback: 'navigateUrl'
            },
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ]
    },
    {
      roleType: 6,
      background: '',
      navView: [
        {
          title: 'Program planner',
          isNested: false,
          route: '/planner',
          image: 'programplannericon.svg',
          class: 'programplannericon',
          isSelected: false,
          dropdownClass: 'purple-theme'
        },
        {
          title: 'Participants',
          isNested: true,
          route: '',
          image: 'scholars.svg',
          class: 'scholars',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'green-list',
          nestedDrop: [
            {
              title: 'Roster',
              isNested: false,
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_ENROLLMENT_REDIRECT_URL}roster`
            },
            {
              title: 'Attendance',
              isNested: false,
              route: '/attendance',
              isSelected: false
            }
          ]
        },
        {
          title: 'Analytics',
          isNested: true,
          route: '',
          image: 'chart.svg',
          class: 'programplannericon',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'analytics-list',
          nestedDrop: [
            {
              title: 'Participant',
              isNested: false,
              isSelected: false,
              route: '/analytics/scholar'
            },
            {
              title: 'Family engagement',
              isNested: false,
              route: '/analytics/familyEngagement',
              isSelected: false
            },
            {
              title: 'Quality',
              isNested: false,
              route: '/analytics/quality',
              isSelected: false
            }
          ]
        },
        {
          title: `${APP_CONST.LEARNING}`,
          image: 'resources.svg',
          class: 'resources',
          dropdownClass: 'dark-blue-list',
          isNested: true,
          route: '',
          openStatus: false,
          isSelected: false,
          nestedDrop: [
            {
              title: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.menuTitle}`,
              isNested: false,
              isSelected: false,
              route: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.path}`
            },
            {
              title: `${APP_CONST.APP_ROUTES.CMS_CATALOG.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.CMS_CATALOG.path}`,
              isSelected: false
            },
            {
              title: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.path}`,
              isSelected: false
            }
          ]
        },
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300px',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'My profile',
              isNested: false,
              route: '',
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_IDENTITY_REDIRECT_URL}user/profile`
            },
            {
              title: 'Account',
              isNested: false,
              route: APP_CONST.APP_ROUTES.ACCOUNT.path,
              isSelected: false
            },
            {
              title: 'Help center',
              isNested: false,
              route: '',
              id: 3,
              callback: 'navigateUrl'
            },
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ],
      learningExperienceDirectAccessNavView: [
        {
          title: `${APP_CONST.LEARNING}`,
          image: 'resources.svg',
          class: 'resources',
          dropdownClass: 'dark-blue-list',
          isNested: true,
          route: '',
          openStatus: false,
          isSelected: false,
          nestedDrop: [
            {
              title: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.path}`,
              isSelected: false
            },
            {
              title: `${APP_CONST.APP_ROUTES.CMS_CATALOG.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.CMS_CATALOG.path}`,
              isSelected: false
            },
            {
              title: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.path}`,
              isSelected: false
            }
          ]
        },
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300px',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'My profile',
              isNested: false,
              route: '',
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_IDENTITY_REDIRECT_URL}user/profile`
            },
            {
              title: 'Account',
              isNested: false,
              route: APP_CONST.APP_ROUTES.ACCOUNT_TEAM.path,
              isSelected: false
            },
            {
              title: 'Help center',
              isNested: false,
              route: '',
              id: 3,
              callback: 'navigateUrl'
            },
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ]
    },
    {
      roleType: 5,
      background: '',
      navView: [
        {
          title: 'Program planner',
          isNested: false,
          route: '/planner',
          image: 'programplannericon.svg',
          class: 'programplannericon',
          isSelected: false,
          dropdownClass: 'purple-theme'
        },
        {
          title: 'Analytics',
          isNested: true,
          route: '',
          image: 'chart.svg',
          class: 'programplannericon',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'analytics-list',
          nestedDrop: [
            {
              title: 'Participant',
              isNested: false,
              isSelected: false,
              route: '/analytics/scholar'
            },
            {
              title: 'Professional development',
              isNested: false,
              route: '/analytics/professionalLearning',
              isSelected: false
            },
            {
              title: 'Family engagement',
              isNested: false,
              route: '/analytics/familyEngagement',
              isSelected: false
            },
            {
              title: 'Quality',
              isNested: false,
              route: '/analytics/quality',
              isSelected: false
            }
          ]
        },
        {
          title: `${APP_CONST.LEARNING}`,
          image: 'resources.svg',
          class: 'resources',
          dropdownClass: 'dark-blue-list',
          isNested: true,
          route: '',
          openStatus: false,
          isSelected: false,
          nestedDrop: [
            {
              title: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.menuTitle}`,
              isNested: false,
              isSelected: false,
              route: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.path}`
            },
            {
              title: `${APP_CONST.APP_ROUTES.CMS_CATALOG.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.CMS_CATALOG.path}`,
              isSelected: false
            },
            {
              title: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.path}`,
              isSelected: false
            }
          ]
        },
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300x',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'My profile',
              isNested: false,
              route: '',
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_IDENTITY_REDIRECT_URL}user/profile`
            },
            {
              title: 'Account',
              isNested: false,
              route: APP_CONST.APP_ROUTES.ACCOUNT.path,
              isSelected: false
            },
            {
              title: 'Help center',
              isNested: false,
              route: '',
              id: 3,
              callback: 'navigateUrl'
            },
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ],
      learningExperienceDirectAccessNavView: [
        {
          title: 'Analytics',
          isNested: true,
          route: '',
          image: 'chart.svg',
          class: 'programplannericon',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'analytics-list',
          nestedDrop: [
            {
              title: 'Professional development',
              isNested: false,
              route: '/analytics/professionalLearning',
              isSelected: false
            }
          ]
        },
        {
          title: `${APP_CONST.LEARNING}`,
          image: 'resources.svg',
          class: 'resources',
          dropdownClass: 'dark-blue-list',
          isNested: true,
          route: '',
          openStatus: false,
          isSelected: false,
          nestedDrop: [
            {
              title: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.path}`,
              isSelected: false
            },
            {
              title: `${APP_CONST.APP_ROUTES.CMS_CATALOG.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.CMS_CATALOG.path}`,
              isSelected: false
            },
            {
              title: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.path}`,
              isSelected: false
            }
          ]
        },
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300px',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'My profile',
              isNested: false,
              route: '',
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_IDENTITY_REDIRECT_URL}user/profile`
            },
            {
              title: 'Account',
              isNested: false,
              route: APP_CONST.APP_ROUTES.ACCOUNT_TEAM.path,
              isSelected: false
            },
            {
              title: 'Help center',
              isNested: false,
              route: '',
              id: 3,
              callback: 'navigateUrl'
            },
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ]
    },
    {
      roleType: 1,
      navView: [
        {
          title: 'Partner accounts',
          isNested: false,
          route: '/home',
          relatedRoutes: [
            APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS.path,
            APP_CONST.APP_ROUTES.ACCOUNT_TEAM.path,
            '/accounts/edit',
            '/accounts/createprogram',
            '/account/program/sites'
          ],
          image: 'homeicon.svg',
          class: 'homeicon',
          isSelected: false,
          dropdownClass: 'voilet'
        },
        {
          title: 'Manage users',
          isNested: false,
          route: '/users',
          image: 'managerusericon.svg',
          class: 'managerusericon',
          isSelected: false
        },
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300px',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'My profile',
              isNested: false,
              route: '',
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_IDENTITY_REDIRECT_URL}user/profile`
            },
            {
              title: 'Help center',
              isNested: false,
              route: '',
              id: 3,
              callback: 'navigateUrl'
            },
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ]
    },
    {
      roleType: 2,
      navView: [
        {
          title: 'Partner accounts',
          isNested: false,
          route: '/home',
          relatedRoutes: [
            APP_CONST.APP_ROUTES.ACCOUNT_PROGRAMS.path,
            APP_CONST.APP_ROUTES.ACCOUNT_TEAM.path,
            '/accounts/edit',
            '/accounts/createprogram',
            '/account/program/sites'
          ],
          image: 'homeicon.svg',
          class: 'homeicon',
          isSelected: false,
          dropdownClass: 'voilet'
        },
        {
          title: 'Manage users',
          isNested: false,
          route: '/users',
          image: 'managerusericon.svg',
          class: 'managerusericon',
          isSelected: false
        },
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300px',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'My profile',
              isNested: false,
              route: '',
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_IDENTITY_REDIRECT_URL}user/profile`
            },
            {
              title: 'Help center',
              isNested: false,
              route: '',
              id: 3,
              callback: 'navigateUrl'
            },
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ]
    },
    {
      roleType: 4,
      navView: [
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300px',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'My profile',
              isNested: false,
              route: '',
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_IDENTITY_REDIRECT_URL}user/profile`
            },
            {
              title: 'Account',
              isNested: false,
              route: APP_CONST.APP_ROUTES.ACCOUNT.path,
              isSelected: false
            },
            {
              title: 'Design settings',
              isNested: false,
              route: '',
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_ENROLLMENT_REDIRECT_URL}design-settings/family-engagement`
            },
            {
              title: 'Family records',
              isNested: false,
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_ENROLLMENT_REDIRECT_URL}families/scholar`
            },
            {
              title: 'Enrollment manager',
              isNested: false,
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_ENROLLMENT_REDIRECT_URL}enroll/submissions`
            },
            {
              title: 'Help center',
              isNested: false,
              route: '',
              id: 3,
              callback: 'navigateUrl'
            },
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ],
      learningExperienceDirectAccessNavView: [
        {
          title: 'Analytics',
          isNested: true,
          route: '',
          image: 'chart.svg',
          class: 'programplannericon',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'analytics-list',
          nestedDrop: [
            {
              title: 'Professional development',
              isNested: false,
              route: '/analytics/professionalLearning',
              isSelected: false
            }
          ]
        },
        {
          title: `${APP_CONST.LEARNING}`,
          image: 'resources.svg',
          class: 'resources',
          dropdownClass: 'dark-blue-list',
          isNested: true,
          route: '',
          openStatus: false,
          isSelected: false,
          nestedDrop: [
            {
              title: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.CMS_MY_CONTENT.path}`,
              isSelected: false
            },
            {
              title: `${APP_CONST.APP_ROUTES.CMS_CATALOG.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.CMS_CATALOG.path}`,
              isSelected: false
            },
            {
              title: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.menuTitle}`,
              isNested: false,
              route: `${APP_CONST.APP_ROUTES.MY_COLLECTIONS.path}`,
              isSelected: false
            }
          ]
        },
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300px',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'My profile',
              isNested: false,
              route: '',
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_IDENTITY_REDIRECT_URL}user/profile`
            },
            {
              title: 'Account',
              isNested: false,
              route: APP_CONST.APP_ROUTES.ACCOUNT_TEAM.path,
              isSelected: false
            },
            {
              title: 'Help center',
              isNested: false,
              route: '',
              id: 3,
              callback: 'navigateUrl'
            },
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ]
    },
    {
      roleType: 0,
      navView: [
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300px',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'My profile',
              isNested: false,
              route: '',
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_IDENTITY_REDIRECT_URL}user/profile`
            },
            {
              title: 'Account',
              isNested: false,
              route: APP_CONST.APP_ROUTES.ACCOUNT.path,
              isSelected: false
            },
            {
              title: 'Family records',
              isNested: false,
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_ENROLLMENT_REDIRECT_URL}families/scholar`
            },
            {
              title: 'Enrollment manager',
              isNested: false,
              isSelected: false,
              baseUrl: `${process.env.VUE_APP_BASE_ENROLLMENT_REDIRECT_URL}enroll/submissions`
            },
            {
              title: 'Help center',
              isNested: false,
              route: '',
              id: 3,
              callback: 'navigateUrl'
            },
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ]
    }
  ];
  public static readonly NAV_DECIDER_Configure_Program = [
    {
      roleType: 1,
      navView: [
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300x',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ]
    },
    {
      roleType: 2,
      navView: [
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300x',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ]
    },
    {
      roleType: 4,
      navView: [
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300x',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ]
    },
    {
      roleType: 5,
      navView: [
        {
          title: '',
          isNested: true,
          route: '',
          image: 'user.svg',
          class: 'profile',
          openStatus: false,
          isSelected: false,
          dropdownClass: 'bright-blue',
          floatRight: true,
          profile: true,
          width: {
            minWidth: '200px',
            maxWidth: '300x',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          hover: false,
          nestedDrop: [
            {
              title: 'Logout',
              isNested: false,
              route: '',
              isSelected: false,
              callback: 'logout',
              removeForMobile: true,
              changableBgBorder: true
            }
          ]
        }
      ]
    }
  ];

  public static IS_MOUNTED = 'isMounted';

  public static readonly VALIDATE_STAFF_FORM = {
    firstName: {
      name: 'firstName',
      error: false,
      errorText: 'Name could not be empty'
    },
    lastName: {
      name: 'lastName',
      error: false,
      errorText: 'Start date could not be empty'
    }
  };

  public static readonly STAFF_ARR_OBJECT = [
    {
      title: 'Add New Staff',
      site: {
        siteId: 0,
        name: '',
        listValues: [],
        value: 'Select',
        errorStatus: false,
        errorText: 'Site/Session Name is required',
        openStatus: false
      },
      firstName: {
        name: 'First Name',
        value: '',
        errorStatus: false,
        errorText: 'First Name is required',
        alphaCharError: false,
        errorAlpha: 'Only Alphabets are allowed',
        minimumCharError: false,
        errorMinimumLength: 'Maximum 20 characters allowed'
      },
      lastName: {
        name: 'Last Name',
        value: '',
        errorStatus: false,
        errorText: 'Last Name is required',
        alphaCharError: false,
        errorAlpha: 'Only Alphabets are allowed',
        minimumCharError: false,
        errorMinimumLength: 'Maximum 20 characters allowed'
      },
      programRole: {
        name: 'Program Role',
        value: '',
        errorStatus: false
      },
      classroom: {
        classroomId: 0,
        name: 'Classroom',
        listValues: [],
        value: 'Select',
        errorStatus: false,
        openStatus: false
      },
      phoneNumber: {
        name: 'Phone Number',
        value: '',
        errorStatus: false,
        errorInvalid: false,
        errorInvalidText:
          'Phone number must be at least 10 digits'
      },
      email: {
        name: 'Email',
        value: '',
        errorStatus: false,
        errorText: 'Email is required',
        errorInvalid: false,
        mandatory: false,
        errorInvalidText: 'Email is invalid',
        duplicateMail: false
      }
    },
    {
      title: 'Emergency Contact Information',
      firstName: {
        name: 'First Name',
        value: '',
        errorStatus: false
      },
      lastName: {
        name: 'Last Name',
        value: '',
        errorStatus: false
      },
      relationship: {
        name: 'Relationship',
        value: '',
        errorStatus: false
      },
      phoneNumber: {
        name: 'Phone Number',
        value: '',
        errorStatus: false,
        errorInvalid: false,
        errorInvalidText:
          'Phone number must be at least 10 digits'
      },
      email: {
        name: 'Email',
        value: '',
        errorStatus: false,
        errorInvalid: false,
        errorInvalidText: 'Email is invalid',
        duplicateMail: false
      }
    },
    {
      title: 'Additional Information',
      customNotes: {
        name: 'Custom Notes',
        value: '',
        errorStatus: false
      },
      allergies: {
        name: 'Allergies/Medical Conditions',
        value: '',
        errorStatus: false
      },
      status: {
        name: 'Status',
        listValues: [
          { value: 'Active', id: 1 },
          { value: 'Inactive', id: 2 }
        ],
        value: { value: 'Active', id: 1 },
        errorStatus: false,
        openStatus: false
      },
      lastLogin: {
        name: 'Last Login',
        value: '',
        errorStatus: false
      },
      programName: {
        name: 'Program Name',
        value: '',
        errorStatus: false
      },
      inactiveDate: {
        name: 'Inactive Date',
        value: '',
        errorStatus: false
      },
      inviteSys: {
        name: 'Invite to system as:',
        value: false,
        errorStatus: false
      },
      roleSelect: {
        name: '',
        listValues: [
          { value: 'Program', id: 5 },
          { value: 'Staff', id: 7 },
          { value: 'Site/Session', id: 6 }
        ],
        value: 'Select Role',
        errorStatus: false,
        openStatus: false,
        disable: true
      }
    }
  ];

  public static readonly dropdownfields = [
    'classroom',
    'site',
    'status',
    'roleSelect'
  ];

  public static WEEK_DAYS = [
    {
      id: 0,
      name: 'Sunday',
      check: false,
      value: 'Su',
      enableDay: ''
    },
    {
      id: 1,
      name: 'Monday',
      check: false,
      value: 'M',
      enableDay: ''
    },
    {
      id: 2,
      name: 'Tuesday',
      check: false,
      value: 'T',
      enableDay: ''
    },
    {
      id: 3,
      name: 'Wednesday',
      check: false,
      value: 'W',
      enableDay: ''
    },
    {
      id: 4,
      name: 'Thursday',
      check: false,
      value: 'Th',
      enableDay: ''
    },
    {
      id: 5,
      name: 'Friday',
      check: false,
      value: 'F',
      enableDay: ''
    },
    {
      id: 6,
      name: 'Saturday',
      check: false,
      value: 'S',
      enableDay: ''
    }
  ];

  public static readonly PANEL_ACCORDIONS_SCHOLAR = [
    {
      textValue: 'Scholar Info',
      openStatus: true,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      firstName: {
        name: 'First Name',
        value: '',
        errorStatus: false,
        errorText: 'First Name is required'
      },
      lastName: {
        name: 'Last Name',
        value: '',
        errorStatus: false,
        errorText: 'Last Name is required'
      },
      enteringGrade: {
        name: 'Grade',
        value: 'Select',
        listValues: [
          'Pre-K',
          'K',
          '1',
          '2',
          '3',
          '4',
          '5',
          '6',
          '7',
          '8',
          '9',
          '10',
          '11',
          '12'
        ],
        dropStatus: false,
        errorStatus: false,
        errorText: 'Grade is required'
      },
      birthDate: {
        name: 'Birth Date',
        value: '',
        errorStatus: false,
        errorText: 'Birth Date is required'
      },
      gender: {
        name: 'Gender',
        value: '',
        listValues: ['Male', 'Female', 'Others'],
        dropStatus: false,
        errorStatus: false,
        errorText: ''
      },
      otherGender: {
        name: 'Other Gender',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      ethnicity: {
        name: 'Race / Ethnicity',
        value: '',
        listValues: ['Male', 'Female', 'Others'],
        dropStatus: false,
        errorStatus: false,
        errorText: ''
      },
      classroomName: {
        name: 'Group',
        value: '',
        listValues: [],
        dropStatus: false,
        errorStatus: false,
        errorText: ''
      }
    },
    {
      textValue: 'Scheduled Days',
      openStatus: false
    },
    {
      textValue: 'Scholar ID',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      scholarStudentId: {
        name: 'School Student ID',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      scholarId: {
        name: 'Scholar ID',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      bellxcelId: {
        name: 'BellXcel ID',
        value: '',
        errorStatus: false,
        errorText: ''
      }
    },
    {
      textValue: 'Status',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      status: {
        name: 'Status',
        value: 'Active',
        dropStatus: false,
        listValues: ['Active', 'Inactive'],
        errorStatus: false,
        errorText: ''
      },
      enrollmentDate: {
        name: 'Enrollment Date',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      inactiveDate: {
        name: 'Inactive Date',
        value: '',
        errorStatus: false,
        errorText: '',
        isHidden: false
      },
      inactiveNotes: {
        name: 'Note',
        value: '',
        errorStatus: false,
        errorText: '',
        isHidden: false
      }
    },
    {
      textValue: 'Address',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      addressLine1: {
        name: 'Address Line 1',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      addressLine2: {
        name: 'Address Line 2',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      city: {
        name: 'City',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      state: {
        name: 'State',
        value: '',
        errorStatus: false,
        errorText: '',
        dropStatus: false
      },
      postalCode: {
        name: 'Postal Code',
        value: '',
        errorStatus: false,
        errorText: 'Postal Code Must be 5 digits'
      }
    },
    {
      textValue: 'Family',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      families: [
        {
          openStatus: true,
          firstName: {
            name: 'First Name',
            value: '',
            errorStatus: false,
            errorText: 'First Name is required'
          },
          lastName: {
            name: 'Last Name',
            value: '',
            errorStatus: false,
            errorText: 'Last Name is required'
          },
          email: {
            name: 'Email',
            value: '',
            errorStatus: false,
            errorText: ''
          },
          allowedToPick: {
            name: 'Authorized Pickup',
            value: '',
            dropStatus: false,
            listValues: ['Yes', 'No'],
            errorStatus: false,
            errorText: ''
          },
          relationship: {
            name: 'Relationship',
            value: '',
            errorStatus: false,
            errorText: ''
          },
          phoneNumber: {
            name: 'Phone Number',
            value: '',
            errorStatus: false,
            errorText: ''
          }
        }
      ]
    },
    {
      textValue: 'Emergency Contact',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      emergencyContacts: [
        {
          openStatus: true,
          firstName: {
            name: 'First Name',
            value: '',
            errorStatus: false,
            errorText: 'First Name is required'
          },
          lastName: {
            name: 'Last Name',
            value: '',
            errorStatus: false,
            errorText: 'Last Name is required'
          },
          email: {
            name: 'Email',
            value: '',
            errorStatus: false,
            errorText: ''
          },
          allowedToPick: {
            name: 'Authorized Pickup',
            value: '',
            dropStatus: false,
            listValues: ['Yes', 'No'],
            errorStatus: false,
            errorText: ''
          },
          relationship: {
            name: 'Relationship',
            value: '',
            errorStatus: false,
            errorText: ''
          },
          phoneNumber: {
            name: 'Phone Number',
            value: '',
            errorStatus: false,
            errorText: ''
          }
        }
      ]
    },
    {
      textValue: 'Medical',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      epiPen: {
        name: 'Epi Pen',
        value: '',
        dropStatus: false,
        listValues: ['a', 'b', 'c'],
        errorStatus: false,
        errorText: ''
      },
      asthmaInhaler: {
        name: 'Asthma Inhaler',
        value: '',
        dropStatus: false,
        listValues: ['a', 'b', 'c'],
        errorStatus: false,
        errorText: ''
      },
      allergies: {
        name: 'Allergies',
        value: '',
        errorStatus: '',
        errorText: ''
      },
      medications: {
        name: 'Medications',
        value: '',
        errorStatus: '',
        errorText: ''
      }
    },
    {
      textValue: 'Learning Status',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      iepStatus: {
        name: 'IEP Status',
        value: '',
        listValues: ['a', 'b', 'c'],
        dropStatus: false,
        errorStatus: false,
        errorText: ''
      },
      ellStatus: {
        name: 'ELL/LEP Status',
        value: '',
        listValues: ['a', 'b', 'c'],
        dropStatus: false,
        errorStatus: false,
        errorText: ''
      },
      specialEducation: {
        name: 'Special Education Status',
        value: '',
        listValues: ['a', 'b', 'c'],
        dropStatus: false,
        errorStatus: '',
        errorText: ''
      }
    },
    {
      textValue: 'Custom Fields',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required'
    },
    {
      textValue: 'Consent Forms',
      openStatus: false,
      textArea: 'Track your consent forms. Enter "Yes" or "No".',
      scholarInformation: {
        name: 'Scholar Information Consent Form',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      fieldTrip: {
        name: 'Field Trip Consent Form',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      madical: {
        name: 'Medical Consent Form',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      emergency: {
        name: 'Emergency Consent Form',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      photoVideoOpt: {
        name: 'Photo/ Video Opt Out',
        value: '',
        errorStatus: false,
        errorText: ''
      }
    },
    {
      textValue: 'Scholar Documentation',
      openStatus: false
    }
  ];

  public static readonly EDIT_PANEL_ACCORDIONS_SCHOLAR = [
    {
      textValue: 'Scholar Info',
      openStatus: true,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      firstName: {
        name: 'First Name',
        value: '',
        errorStatus: false,
        errorText: 'First Name is required'
      },
      lastName: {
        name: 'Last Name',
        value: '',
        errorStatus: false,
        errorText: 'Last Name is required'
      },
      enteringGrade: {
        name: 'Entering Grade',
        value: 'Select',
        listValues: [
          'Pre-K',
          'K',
          '1',
          '2',
          '3',
          '4',
          '5',
          '6',
          '7',
          '8',
          '9',
          '10',
          '11',
          '12'
        ],
        dropStatus: false,
        errorStatus: false,
        errorText: 'Entering Grade is required'
      },
      birthDate: {
        name: 'Birth Date',
        value: '',
        errorStatus: false,
        errorText: 'Birth Date is required'
      },
      gender: {
        name: 'Gender',
        value: 'Select',
        listValues: ['Male', 'Female', 'Others'],
        dropStatus: false,
        errorStatus: false,
        errorText: ''
      },
      otherGender: {
        name: 'Other Gender',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      ethnicity: {
        name: 'Ethnicity',
        value: 'Select',
        listValues: ['Male', 'Female', 'Others'],
        dropStatus: false,
        errorStatus: false,
        errorText: ''
      },
      classroom: {
        name: 'Classroom Name',
        value: 'Select',
        listValues: ['Apple', 'Mango', 'Others'],
        dropStatus: false,
        errorStatus: false,
        errorText: ''
      }
    },
    {
      textValue: 'Scheduled Days',
      openStatus: false
    },
    {
      textValue: 'Scholar ID',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      scholarStudentId: {
        name: 'School Student ID',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      scholarId: {
        name: 'Scholar ID',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      bellxcelId: {
        name: 'BellXcel ID',
        value: '',
        errorStatus: false,
        errorText: ''
      }
    },

    {
      textValue: 'Status',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      status: {
        name: 'Status',
        value: 'Select',
        dropStatus: false,
        listValues: ['Active', 'Inactive'],
        errorStatus: false,
        errorText: ''
      },
      enrollmentDate: {
        name: 'Enrollment Date',
        value: '',
        errorStatus: false,
        errorText: ''
      }
    },

    {
      textValue: 'Address',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      addressLine1: {
        name: 'Address Line 1',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      addressLine2: {
        name: 'Address Line 2',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      city: {
        name: 'City',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      state: {
        name: 'State',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      postalCode: {
        name: 'Postal Code',
        value: '',
        errorStatus: false,
        errorText: ''
      }
    },

    {
      textValue: 'Family',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      families: [
        {
          openStatus: true,
          firstName: {
            name: 'First Name',
            value: '',
            errorStatus: false,
            errorText: 'First Name is required'
          },
          lastName: {
            name: 'Last Name',
            value: '',
            errorStatus: false,
            errorText: 'Last Name is required'
          },
          email: {
            name: 'Email',
            value: '',
            errorStatus: false,
            errorText: ''
          },
          allowedToPick: {
            name: 'Authorized Pickup',
            value: 'Select',
            dropStatus: false,
            listValues: ['Yes', 'No'],
            errorStatus: false,
            errorText: ''
          },
          relationship: {
            name: 'Relationship',
            value: '',
            errorStatus: false,
            errorText: ''
          },
          phoneNumber: {
            name: 'Phone Number',
            value: '',
            errorStatus: false,
            errorText: ''
          }
        }
      ]
    },

    {
      textValue: 'EmergencyContact',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      emergencyContacts: [
        {
          openStatus: true,
          firstName: {
            name: 'First Name',
            value: '',
            errorStatus: false,
            errorText: 'First Name is required'
          },
          lastName: {
            name: 'Last Name',
            value: '',
            errorStatus: false,
            errorText: 'Last Name is required'
          },
          email: {
            name: 'Email',
            value: '',
            errorStatus: false,
            errorText: ''
          },
          allowedToPick: {
            name: 'Authorized Pickup',
            value: 'Select',
            dropStatus: false,
            listValues: ['Yes', 'No'],
            errorStatus: false,
            errorText: ''
          },
          relationship: {
            name: 'Relationship',
            value: '',
            errorStatus: false,
            errorText: ''
          },
          phoneNumber: {
            name: 'Phone Number',
            value: '',
            errorStatus: false,
            errorText: ''
          }
        }
      ]
    },

    {
      textValue: 'Medical',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      epiPen: {
        name: 'Epi Pen',
        value: 'Select',
        dropStatus: false,
        listValues: ['a', 'b', 'c'],
        errorStatus: false,
        errorText: ''
      },
      asthmaInhaler: {
        name: 'Asthma Inhaler',
        value: 'Select',
        dropStatus: false,
        listValues: ['a', 'b', 'c'],
        errorStatus: false,
        errorText: ''
      },
      allergies: {
        name: 'Allergies',
        value: '',
        errorStatus: '',
        errorText: ''
      },
      medications: {
        name: 'Medications',
        value: '',
        errorStatus: '',
        errorText: ''
      }
    },

    {
      textValue: 'Learning Status',
      openStatus: false,
      textArea:
        'Perhaps a few lines of instruction here. All starred(*) fields are required',
      iepStatus: {
        name: 'IEP Status',
        value: 'Select',
        listValues: ['a', 'b', 'c'],
        dropStatus: false,
        errorStatus: false,
        errorText: ''
      },
      ellStatus: {
        name: 'ELL/LEP Status',
        value: 'Select',
        listValues: ['a', 'b', 'c'],
        dropStatus: false,
        errorStatus: false,
        errorText: ''
      },
      specialEducation: {
        name: 'Special Education Status',
        value: 'Select',
        listValues: ['a', 'b', 'c'],
        dropStatus: false,
        errorStatus: '',
        errorText: ''
      }
    },
    {
      textValue: 'Consent Forms',
      openStatus: false,
      textArea: 'Track your consent forms. Enter "Yes" or "No".',
      scholarInformation: {
        name: 'Scholar Information Consent Form',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      fieldTrip: {
        name: 'Field Trip Consent Form',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      madical: {
        name: 'Medical Consent Form',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      emergency: {
        name: 'Emergency Consent Form',
        value: '',
        errorStatus: false,
        errorText: ''
      },
      photoVideoOpt: {
        name: 'Photo/ Video Opt Out',
        value: '',
        errorStatus: false,
        errorText: ''
      }
    },
    {
      textValue: 'Scholar Documentation',
      openStatus: false
    }
  ];
  public static readonly LINK_OBJ = {
    openStatus: true,
    lastName: {
      name: 'Last Name',
      value: '',
      errorStatus: false,
      errorText: 'Last Name is required'
    },
    firstName: {
      name: 'First Name',
      value: '',
      errorStatus: false,
      errorText: 'First Name is required'
    },
    email: {
      name: 'Email',
      value: '',
      errorStatus: false,
      errorText: ''
    },
    allowedToPick: {
      name: 'Authorized Pickup',
      value: '',
      dropStatus: false,
      listValues: ['Yes', 'No'],
      errorStatus: false,
      errorText: ''
    },
    relationship: {
      name: 'Relationship',
      value: '',
      errorStatus: false,
      errorText: ''
    },
    phoneNumber: {
      name: 'Phone Number',
      value: '',
      errorStatus: false,
      errorText: ''
    }
  };
  public static readonly TAB_SELECTION = [
    {
      id: 'ada',
      class: 'distance-btw',
      active: true,
      title: 'Average Daily Attendance'
    },
    {
      id: 'scholar-retention',
      class: '',
      active: false,
      title: 'Scholar Retention'
    }
  ];

  public static readonly END_TIME = ' 23:59';
  public static readonly PRIVACY_POLICY =
    'https://learn.arly.com/privacy-policy';

  public static readonly SITE_FORM_FIELDS: SiteFormFields = {
    openStatus: APP_CONST.TRUE,
    deletePopup: APP_CONST.FALSE,
    siteName: {
      value: '',
      errorStatus: ''
    },
    siteAddress: {
      value: '',
      errorStatus: ''
    },
    siteAddress2: {
      value: '',
      errorStatus: ''
    },
    city: {
      value: '',
      errorStatus: ''
    },
    state: {
      value: '',
      errorStatus: ''
    },
    postalCode: {
      value: '',
      errorStatus: ''
    },
    siteStartDate: {
      value: '',
      errorStatus: '',
      siteStartDateErr: false
    },
    siteEndDate: {
      value: '',
      errorStatus: '',
      siteEndDateErr: false
    },
    selectedDays: {
      value: '',
      errorStatus: ''
    }
  };

  public static readonly IGNORE_NAV_ON_ROUTES: string[] = [
    '/welcome'
  ];
  public static readonly IGNORE_NAV_ON_ROUTELIST: string[] = [
    'Welcome To Bellxcel'
  ];
  public static readonly LOGO_NAV = {
    roleType: 7,
    navView: [
      {
        title: 'Home',
        isNested: false,
        route: '/home',
        image: 'homeicon.svg',
        class: 'homeicon'
      }
    ]
  };
  public static readonly STAFF_ROSTER = 'Staff Roster';
  public static readonly MANAGE_PROGRAM = 'Manage Program';
  public static readonly FEATURE_ACCESS_PERMISSION = {
    plannerComponent: {
      name: 'planner component',
      feature: [
        {
          name: 'Staff Navigation access',
          role: [1, 2, 3, 4, 5, 6, 7]
        }
      ]
    },
    plannerComponentAddEventButton: {
      name: 'planner component',
      feature: [{ name: 'ADD_EVENT', role: [1, 2, 3, 4, 5, 6] }]
    }
  };
  public static readonly COLORS: string[] = [
    '#673065',
    '#a56aa3',
    '#ff9f75',
    '#ffb733',
    '#3dae2b',
    '#376300',
    '#6b9efa',
    '#ae65ed',
    '#408fb0',
    '#09729f'
  ];
  public static readonly ACCOUNT_ADMIN_PROGRAM_LIST = { id: 0 };
  public static readonly ACCOUNT_ADMIN_PROGRAMS_LIST = {
    id: 0,
    userId: 0,
    filterKey: 0
  };
  public static readonly USER_INFORMATION = {
    userId: 0,
    programId: 0
  };
  public static readonly ASSIGN_TASKS = {
    taskIds: [0],
    sites: [{ siteId: 0, startDate: '', endDate: '' }],
    programId: 'number'
  };
  public static readonly GET_TASK_STATUSES = [
    {
      index: 0,
      label: 'Active',
      path: 'active-icon.svg',
      value: '1',
      alt: 'Active'
    },
    {
      index: 1,
      label: 'Done',
      path: 'done-status.svg',
      value: '2',
      alt: 'Done'
    },
    {
      index: 2,
      label: 'Skip',
      path: 'skip-icon.svg',
      value: '3',
      alt: 'Skip'
    }
  ];
  public static readonly GET_TASK_MENUS = [
    {
      label: 'Task Item',
      sortField: 'name',
      order: 0,
      class: 'calc_width'
    },
    {
      label: 'Due Date',
      sortField: 'taskEndDate',
      order: 1,
      class: 'wd_160'
    },
    {
      label: 'Owner',
      sortField: 'taskUsers',
      order: 0,
      class: 'wd_160'
    },
    {
      label: 'Status',
      sortField: 'status',
      order: 0,
      class: 'wd_160'
    }
  ];
  public static readonly GET_EVENT_MENUS = [
    {
      label: 'Date',
      sortField: 'event_start_timestamp',
      order: 2,
      class: 'date'
    },
    {
      label: 'Event Name',
      sortField: 'name',
      order: 0,
      class: 'event'
    },
    {
      label: 'Start Time',
      sortField: 'event_start_timestamp',
      order: 0,
      class: 'time'
    },
    {
      label: 'End Time',
      sortField: 'event_end_timestamp',
      order: 0,
      class: 'time'
    },
    {
      label: 'Location',
      sortField: 'location',
      order: 0,
      class: 'location'
    }
  ];
  public static readonly GET_MOBILE_EVENT_MENUS = [
    {
      label: 'Date',
      sortField: 'event_start_timestamp',
      order: 2,
      class: 'date'
    },
    {
      label: 'Event Name',
      sortField: 'name',
      order: 0,
      class: 'event'
    }
  ];
  public static readonly SECTIONS = [
    {
      id: 'right-panel',
      title: 'Notes',
      img: 'file-icon.svg',
      img_mobile: 'file-icon-mobile.svg',
      activeImg: 'file-icon-active.svg',
      activeImg_mobile: 'file-icon-active-mobile.svg',
      active: true
    },
    {
      id: 'discuss',
      title: 'Discussion',
      img: 'comment-text.svg',
      img_mobile: 'comment-text.svg',
      activeImg: 'discussion-selected.svg',
      activeImg_mobile: 'comment-text-active.svg',
      active: false
    },
    {
      id: 'own',
      title: 'Owner',
      img: 'group.svg',
      img_mobile: 'group-mobile.svg',
      activeImg: 'group-active.svg',
      activeImg_mobile: 'group-active-mobile.svg',
      active: false
    },
    {
      id: 'collab',
      title: 'Collaborators',
      img: 'various-items.svg',
      img_mobile: 'various-items-mobile.svg',
      activeImg: 'various-items-active.svg',
      activeImg_mobile: 'various-items-active-mobile.svg',
      active: false
    }
  ];
  public static readonly PROGRAM_TASK = {
    id: 0,
    programId: 0,
    name: '',
    startDate: '',
    endDate: '',
    status: 1,
    lastEditedBy: 0,
    lastEditedOn: '',
    userIsTaskOwnerOrCollaborator: false,
    taskNote: {
      id: 0,
      taskId: 0,
      taskNote: ''
    },
    taskDiscussions: [],
    taskUsers: [],
    taskFiles: [],
    fileIds: [],
    deletedFileIds: []
  };
  public static readonly TASK = 1;
  public static readonly GET_TASK_STATUS = [
    {
      label: 'Active',
      class: 'calc_width',
      checked: true,
      path: 'status-active-icon.svg',
      alt: 'active-icon',
      order: 1,
      value: 1
    },
    {
      label: 'Done',
      class: 'wd_160',
      checked: false,
      path: 'done-status.svg',
      alt: 'done-icon',
      order: 2,
      value: 2
    }
  ];
  public static readonly TASK_FORM = {
    name: {
      name: 'name',
      error: false,
      errorText: 'Whoops! Task name is missing.'
    },
    startDate: {
      name: 'startDate',
      error: false,
      errorText: 'Whoops! Start date is missing.'
    },
    endDate: {
      name: 'endDate',
      error: false,
      errorText: 'Whoops! Due date is missing.'
    }
  };

  public static readonly TAB_SELECTION_DASHBOARD_SIDEPANEL = [
    { id: 'scholar', active: true, title: 'SCHOLAR' }
  ];
  public static readonly TAB_SELECTION_DASHBOARD_SIDEPANEL_ROLE_BASED = [
    { id: 'scholar', active: true, title: 'SCHOLAR' },
    { id: 'quality', active: false, title: 'QUALITY' }
  ];
  public static readonly SCHOLARS =
    'Daily Attendance by Scholar_';
  public static readonly SITE =
    'Average Daily Attendance by Site_';
  public static readonly WEEK =
    'Average Daily Attendance by Week_';
  public static readonly SHOLAR_DETAILS =
    'Average Daily Attendance with Scholar Details_';
  public static readonly FILE_HEADER_OF_WEEK =
    ',Week 1 (11/12/20-11/16/20),Week 1 Attendance Completion,Week 2 (11/19/20-11/23/20),Week 2 Attendance Completion,	Week 3 (11/26/20-11/30/20),Week 3 Attendance Completion';
  public static readonly FILE_HEADER_OF_SCHOLAR =
    'Last Name,First Name,Group,Notes,Monday (9/7/2020),Tuesday (9/8/2020),Wed 12/2/20';
  public static readonly FILE_HEADER_WITH_SCHOLAR_DETAILS =
    'First Name, Last Name,Total Positive Attendance Days,Total Possible Attendance Days,Average Daily Attendance,Attendance Completion %';
  public static readonly CONSOLIDATED_SCHOLARS =
    'Scholar_Roster_';
  public static readonly FILE_HEADER_CONSOLIDATED_SCHOLAR =
    'Site, First Name, Last Name,	Nickname, Birth Date, Grade, Scheduled Days, School Student ID, External Student ID, Group, Gender,	Race/Ethnicity,	Home #,	Address 1, Address 2, City,	State, Zip,	Scholar Email, School Name,	Free/Reduced Priced Meals, Language at Home, Pre-Test, Post Test, T-shirt Size, Enrollment Status, Enrollment Date,	Guardian 1: First Name,	Guardian 1: Last Name, Guardian 1: Phone Number, Guardian 1: Phone Number Type,	Guardian 1: Email, Guardian 1: Relationship to Child, Guardian 2: First Name, Guardian 2: Last Name, Guardian 2: Phone Number, Guardian 2: Phone Number Type, Guardian 2: Email, Guardian 2: Relationship to Child,	Authorized 1: First Name, Authorized 1: Last Name, Authorized 1: Phone Number, Authorized 1: Phone Number Type,	Authorized 1: Relationship to Child, Authorized 2: First Name, Authorized 2: Last Name,	Authorized 2: Phone Number,	Authorized 2: Phone Number Type, Authorized 2: Relationship to Child, Authorized 3: First Name,	Authorized 3: Last Name, Authorized 3: Phone Number, Authorized 3: Phone Number Type, Authorized 3: Relationship to Child, Authorized 4: First Name, Authorized 4: Last Name, Authorized 4: Phone Number, Authorized 4: Phone Number Type, Authorized 4: Relationship to Child,	Authorized 5: First Name, Authorized 5: Last Name, Authorized 5: Phone Number, Authorized 5: Phone Number Type,	Authorized 5: Relationship to Child, Emergency 1: First Name, Emergency 1: Last Name, Emergency 1: Phone Number, Emergency 1: Phone Number Type, Emergency 1: Relationship to Child, Emergency 2: First Name, Emergency 2: Last Name, Emergency 2: Phone Number, Emergency 2: Phone Number Type, Emergency 2: Relationship to Child, Emergency 3: First Name, Emergency 3: Last Name, Emergency 3: Phone Number, Emergency 3: Phone Number Type, Emergency 3: Relationship to Child	, Emergency 4: First Name, Emergency 4: Last Name, Emergency 4: Phone Number, Emergency 4: Phone Number Type, Emergency 4: Relationship to Child, Emergency 5: First Name, Emergency 5: Last Name, Emergency 5: Phone Number, Emergency 5: Phone Number Type, Emergency 5: Relationship to Child, Arrival Plan, Dismissal Plan, Epi Pen, Asthma Inhaler, Allergies,	Medications, IEP Status, ELL/LEP Status, Special Education Status';
  public static readonly FILE_HEADER_OF_SITE =
    ',Total Positive Attendance Days,Total Possible Attendance Days,Average Daily Attendance,Attendance Completion %';
  public static readonly WHAT_CAN_UPLOAD_TEXT_ID =
    'files-allowed-link';
  public static readonly SALEFORCE_ACCOUNT_ID =
    'salesforceAccountId';

  public static readonly TASK_STATUS_SKIP = 3;
  public static readonly TASK_STATUS_ACTIVE = 1;
  public static readonly SALESFORCE_ID_MIN_LENGTH = 15;
  public static readonly SALESFORCE_ID_MAX_LENGTH = 20;
  public static readonly CUSTOM_TOOLBAR = [
    ['bold', 'italic', { color: [] }, 'underline', 'strike'],
    [
      { list: 'bullet' },
      { list: 'ordered' },
      { indent: '-1' },
      { indent: '1' }
    ],
    ['link']
  ];
  public static readonly QUALITY_SCORE_OBJ = {
    programId: {
      value: 0,
      errorStatus: false
    },
    siteId: {
      value: 0,
      errorStatus: false
    },
    evaluaterName: {
      value: '',
      errorStatus: false
    },
    assessmentDate: {
      value: '',
      errorStatus: false,
      dateErrorStatus: false
    },
    score: {
      value: '',
      errorStatus: false
    },
    notes: {
      value: '',
      errorStatus: false
    },
    site: {
      value: '',
      errorStatus: false
    },
    id: {
      value: 0,
      errorStatus: false
    },
    week: {
      value: 0,
      errorStatus: false
    }
  };
  public static readonly QUALITY_REQ_KEY = [
    'siteId',
    'assessmentDate',
    'score'
  ];
  public static readonly QUALITY_SCORES_CSV =
    'Quality Check_Scores_';
  public static readonly QUALITY_SCORES = 'Quality Scores';

  public static readonly STAFF_STEPPER = [
    {
      title: 'Upload Staff Roster',
      component: 'upload-file',
      class: 'active'
    },
    {
      title: 'Match Columns',
      component: 'match-columns',
      class: ''
    },
    {
      title: 'Review & Complete',
      component: 'repair-table',
      class: ''
    }
  ];

  public static readonly SCHOLAR_GROUP_DEFAULT = [
    { classroomName: 'All Scholars', id: 0, groupVal: 1 },
    { classroomName: 'Unassigned', id: 0, groupVal: 2 }
  ];
  public static readonly ORDER_ASC_TEXT = 'asc';
  public static readonly ORDER_DESC_TEXT = 'desc';
  public static readonly ORDER_ASC_NUMBER = 1;
  public static readonly ORDER_DESC_NUMBER = 2;
  public static readonly STATUS_ACTIVE = 1;
  public static readonly STATUS_INACTIVE = 2;

  public static readonly APPLICATION_STATUS_ARRAY = [
    '',
    'ARCHIVED',
    'CLOSED',
    'DRAFT',
    'OPEN'
  ];
  public static readonly APPLICATION_STATUS_DD_ARRAY = [
    { id: 4, value: 'Open' },
    { id: 2, value: 'Closed' },
    { id: 3, value: 'Draft' },
    { id: 1, value: 'Archived' }
  ];

  public static readonly TOOLBAR_KEYS = {
    ADD: 'Add',
    RESET_PASSWORD: 'Reset Password',
    DELETE: 'Delete',
    ADD_PRIVILEGES: 'Add Privileges',
    SEND_INVITATION: 'Send Invitation',
    REVOKE_ACCESS: 'Revoke Access',
    UPLOAD_CSV: 'Upload CSV'
  };

  public static readonly ACTIONS_ACCOUNT_DROP = [
    {
      name: 'Add',
      disable: false
    },
    {
      name: 'Reset Password',
      disable: false
    },
    {
      name: 'Delete',
      disable: false
    },
    {
      name: 'Add Privileges',
      disable: true
    },
    {
      name: 'Send Invitation',
      disable: false
    },
    {
      name: 'Revoke Access',
      disable: false
    },
    {
      name: 'Upload CSV',
      disable: false
    }
  ];
  public static readonly USERS_HEADER = {
    FIXED: [
      {
        name: 'First Name',
        value: 'firstName',
        action: true,
        isOpen: false,
        width: '230px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Last Name',
        value: 'lastName',
        action: true,
        isOpen: false,
        width: '230px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      }
    ],
    MOVABLE: [
      {
        name: 'System Role',
        value: 'systemRole',
        action: true,
        isOpen: false,
        display: true,
        filterKey: 'systemRole',
        width: '217px',
        isSort: true,
        isSearch: true,
        isSearchList: true
      },
      {
        name: 'Invitation Status',
        value: 'invitationStatus',
        action: true,
        isOpen: false,
        display: true,
        filterKey: 'invitationStatus',
        width: '145px',
        isSort: true,
        isSearch: true,
        isSearchList: true
      },
      {
        name: 'Invitation Date',
        value: 'invitationDate',
        action: true,
        isOpen: false,
        display: true,
        filterKey: 'invitationDate',
        width: '145px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Programs',
        value: 'programName',
        action: true,
        isOpen: false,
        display: true,
        filterKey: 'programs',
        width: '208.4px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Last Login',
        value: 'lastLogin',
        action: true,
        isOpen: false,
        display: true,
        filterKey: 'lastLogin',
        width: '150px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Site/Sessions',
        value: 'siteName',
        action: true,
        isOpen: false,
        display: false,
        filterKey: 'site/Sessions',
        width: '190px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Position',
        value: 'position',
        action: true,
        isOpen: false,
        display: false,
        filterKey: 'position',
        width: '192px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Email',
        value: 'email',
        action: true,
        isOpen: false,
        display: false,
        filterKey: 'email',
        width: '190px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Phone',
        value: 'phone',
        action: true,
        isOpen: false,
        display: false,
        filterKey: 'phoneType',
        width: '190px',
        isSort: false,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Revoke Date',
        value: 'revokeDate',
        action: true,
        isOpen: false,
        display: false,
        filterKey: 'revokeDate',
        width: '180px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      }
    ]
  };
  public static readonly SITES_ACTIONS_DROPDOWN: ActionDropdownItem[] = [
    {
      name: 'Add',
      disable: false
    },
    {
      name: 'Delete',
      disable: true
    }
  ];
  public static readonly PROGRAMS_ACTIONS_DROPDOWN = [
    {
      name: 'Add',
      disable: false
    },
    {
      name: 'Disable',
      disable: true
    },
    {
      name: 'Enable',
      disable: true
    },
    {
      name: 'Reset Tasks',
      disable: true
    }
  ];
  public static readonly ADD_SITE_DETAILS = {
    siteId: {
      errorStatus: false,
      errorText: '',
      title: 'ID',
      value: 0,
      required: false
    },
    siteName: {
      errorStatus: false,
      errorText: '',
      title: 'Site/Session Name',
      value: '',
      required: true,
      duplicateErrorStatus: false
    },
    startDate: {
      errorStatus: false,
      errorText: '',
      title: 'Start Date',
      value: '',
      required: true
    },
    endDate: {
      errorStatus: false,
      errorText: '',
      title: 'End Date',
      value: '',
      required: true
    },
    siteAddress: {
      errorStatus: false,
      errorText: '',
      title: 'Address Line 1',
      value: '',
      required: true
    },
    siteAddress2: {
      errorStatus: false,
      errorText: '',
      title: 'Address Line 2',
      value: '',
      required: false
    },
    city: {
      errorStatus: false,
      errorText: '',
      title: 'City',
      value: '',
      required: true
    },
    state: {
      errorStatus: false,
      errorText: '',
      title: 'State',
      value: '',
      required: true,
      dropDownStatus: false
    },
    postalCode: {
      errorStatus: false,
      errorText: '',
      title: 'ZIP Code',
      value: '',
      required: true
    },
    status: {
      errorStatus: false,
      errorText: '',
      title: '',
      value: 0,
      required: false
    },
    noOfScholars: {
      errorStatus: false,
      errorText: '',
      title: '',
      value: 0,
      required: false
    },
    startTime: {
      isTimeFormat: false,
      dropDownStatus: false,
      errorStatus: false,
      errorText: '',
      title: 'Start Time',
      value: 'Select',
      required: false
    },
    endTime: {
      isTimeFormat: false,
      dropDownStatus: false,
      errorStatus: false,
      errorText: '',
      title: 'End Time',
      value: 'Select',
      required: false
    },
    isTimeAdded: {
      errorStatus: false,
      errorText: '',
      title: '',
      value: false,
      required: false
    },
    scholarCapacity: {
      errorStatus: false,
      errorText: '',
      title: 'Scholar Capacity',
      value: null,
      required: true
    },
    autoEnrollmentCapacity: {
      errorStatus: false,
      errorText: '',
      title: 'Auto-Enrollment Capacity',
      value: null,
      required: true
    },
    waitlistCapacity: {
      errorStatus: false,
      errorText: '',
      title: 'Waitlist Capacity',
      value: null,
      required: false
    },
    ageFrom: {
      errorStatus: false,
      errorText: '',
      title: 'Age Range',
      value: null,
      required: false
    },
    ageTo: {
      errorStatus: false,
      errorText: '',
      title: '',
      value: null,
      required: false
    },
    ageAsOf: {
      errorStatus: false,
      errorText: '',
      title: 'Age as of',
      value: '',
      required: true
    },
    autoEnroll: {
      toggleStatus: false,
      errorStatus: false,
      errorText: '',
      title: 'Auto-Enrollment',
      value: false,
      required: false
    },
    autoEnrollCapacity: {
      errorStatus: false,
      errorText: '',
      title: '',
      value: null,
      required: false
    },
    waitListCapacity: {
      errorStatus: false,
      errorText: '',
      title: '',
      value: null,
      required: false
    },
    siteDays: {
      errorStatus: false,
      errorText: '',
      siteDaysTexts: [
        'Sun',
        'Mon',
        'Tue',
        'Wed',
        'Thu',
        'Fri',
        'Sat'
      ],
      title:
        'Please select the days your program will meet at this site/session',
      viewTitle: 'Site/Session Days:',
      value: [],
      required: true
    }
  };
  public static readonly USERS_OBJ = {
    firstName: '',
    lastName: '',
    invitationStatus: '',
    invitationDate: '',
    programs: '',
    lastlogin: '',
    sitesessions: '',
    position: '',
    email: '',
    phoneNumber: '',
    revoke: '',
    systemRole: ''
  };
  public static readonly ATTENDANCE_STATUS = [
    '--',
    'Absent',
    'Present',
    'Late',
    'Not Scheduled'
  ];
  public static readonly CHECKOUT_ATTENDANCE_STATUS = [
    '--',
    'Dismissed'
  ];

  public static readonly PHONE_TYPE_OBJ = [
    'Cell',
    'Work',
    'Home'
  ];

  public static readonly POSITION_OBJ = [
    'Teacher',
    'Admin',
    'Support Staff',
    'Counselor',
    'Other'
  ];

  public static readonly SITE_SESSION_PAYLOAD = [];
  public static readonly ACCOUNTS_ACTIONS_DROPDOWN = [
    {
      name: 'Add',
      disable: false
    },
    {
      name: 'Disable',
      disable: true
    },
    {
      name: 'Enable',
      disable: true
    }
  ];

  public static readonly TEAM_ROSTER_PAYLOAD = {
    ProgramId: 0,
    SiteId: 0,
    Id: 1,
    Active: 0,
    SearchField: '',
    SearchValue: '',
    Search: '',
    RoleType: 0,
    Page: 1,
    Count: 25,
    SortField: '',
    SortDir: 0,
    isExactMatch: false
  };
  public static readonly TEAM_ROSTER_HEADER = {
    FIXED: [
      {
        name: 'First Name',
        value: 'firstName',
        action: true,
        isOpen: false,
        width: '235px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Last Name',
        value: 'lastName',
        action: true,
        isOpen: false,
        width: '235px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      }
    ],
    MOVABLE: [
      {
        name: 'System Role',
        value: 'systemRole',
        action: true,
        isOpen: false,
        display: true,
        filterKey: 'systemRole',
        width: '261px',
        isSort: true,
        isSearch: true,
        isSearchList: true
      },
      {
        name: 'Position',
        value: 'position',
        action: false,
        isOpen: false,
        display: true,
        filterKey: 'position',
        width: '261px',
        isSort: false,
        isSearch: false,
        isSearchList: false
      },
      {
        name: 'Programs',
        value: 'program',
        action: false,
        isOpen: false,
        display: false,
        filterKey: 'program',
        width: '261px',
        isSort: false,
        isSearch: false,
        isSearchList: false
      },
      {
        name: 'Site/Session ',
        value: 'sitesessions',
        action: true,
        isOpen: false,
        display: false,
        filterKey: 'sitesessions',
        width: '261px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Phone',
        value: 'phoneNumber',
        action: true,
        isOpen: false,
        display: false,
        filterKey: 'phoneNumber',
        width: '261px',
        isSort: false,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Email',
        value: 'email',
        action: true,
        isOpen: false,
        display: false,
        filterKey: 'email',
        width: '261px',
        isSort: false,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Invite Status',
        value: 'inviteStatus',
        action: true,
        isOpen: false,
        display: false,
        filterKey: 'inviteStatus',
        width: '261px',
        isSort: true,
        isSearch: true,
        isSearchList: true
      }
    ]
  };

  public static readonly USER_DROPDOWN_LIST = {
    invitationStatus: [
      'Active',
      'Inactive',
      'Deleted',
      'InviteExpired',
      'InvitePending'
    ],
    systemRole: [
      'Primary Account Admin',
      'Account Admin',
      'Program Admin',
      'Session Admin',
      'Staff',
      'No Access'
    ]
  };

  public static readonly SCHEDULE_START_DATE_ERROR_MESSAGE =
    'Schedule date should be greater than or equal to Start date';
  public static readonly SCHEDULE_END_DATE_ERROR_MESSAGE =
    'Schedule date should be smaller than End date';
  public static readonly ANALYTICS_SCHOLAR_NAVIGATION_TAB = [
    {
      name: 'Enrollment',
      key: 'enrollment',
      url: '/analytics/scholar/enrollment'
    },
    {
      name: 'Application',
      key: 'application',
      url: '/analytics/scholar/application'
    },
    {
      name: 'Attendance',
      key: 'attendance',
      url: '/analytics/scholar/attendance'
    },
    {
      name: 'Retention',
      key: 'retention',
      url: '/analytics/scholar/retention'
    }
  ];

  public static readonly ANALYTICS_SCHOLAR_ATTENDANCE_SWITCH_TAB = [
    {
      name: 'ADA',
      url: '/analytics/scholar/attendance/ada'
    },
    {
      name: 'Completion',
      url: '/analytics/scholar/attendance/completion'
    }
  ];

  public static readonly DAY_WEEK_TABS = [
    {
      name: 'Day'
    },
    {
      name: 'Week'
    }
  ];

  public static readonly ANALYTICS_FAMILY_ENGAGEMENT_TAB = [
    {
      name: 'Family',
      key: 'family',
      url: '/analytics/familyEngagement/family'
    },
    {
      name: 'Team',
      key: 'team',
      url: '/analytics/familyEngagement/team'
    }
  ];

  public static readonly IS_CHECKED = 'isChecked';
  public static readonly IS_SELECTED_BY_OTHER_ROLE =
    'isSelectedByOtherUserRole';
  public static readonly OTHER_POSITION = 'Other';
  public static readonly SITE_ALREADY_EXISTS =
    'Error_SiteAlreadyExists';
  public static readonly BLANK_PHONE_NUMBER = '--';
  public static readonly SELECT_OBJECT = {
    id: 0,
    name: 'Select'
  };
  public static readonly SUBSCRIPTION = 'subscription';
  public static readonly SEND_INVITATION = 'sendInvitation';
  public static readonly SEND_INVITATION_CONFIRMATION_TEXT =
    'You have successfully scheduled your invitations!';
  public static readonly SEND_INVITATION_STRIP_INDICATOR =
    'sendInvitationStripIndicator';
  public static readonly INVITATION_SEND_PAYLOAD = {
    userId: [],
    scheduleDate: ''
  };
  public static readonly CONTENTFUL_ESSENTIALS_SUBSCRIPTION_ID =
    '2uJtmv9oVwpbuARhDD7SMn';
  public static readonly CONTENTFUL_SUBSCRIPTION_ORDER = [
    'Essentials',
    'Explorers by Arly',
    'Fitness by Arly',
    'Scholastic Extended ELA',
    'Scholastic Extended Math',
    'Scholastic Extended Plus ELA',
    'Scholastic Extended Plus Math',
    'Scholastic Summer ELA',
    'Scholastic Summer Math',
    'Scholastic Summer PreK',
    'Star Assessment',
    'Enrichment',
    'Jefferson County Explorers',
    'Miami-Dade Explorers',
    'Miami-Dade Summer',
    'NYC Star',
    'NYC Summer'
  ];
  public static readonly THE_ESSENTIALS_CATEGORY =
    'The Essentials';
  public static readonly ENRICHMENT_CATEGORY = 'Enrichment';
  public static readonly ACADEMIC_CATEGORY = 'Academic';
  public static readonly ASSESSMENT_CATEGORY = 'Assessment';
  public static readonly THE_ESSENTIALS_SUBSCRIPTIONS = [
    'The Essentials'
  ];
  public static readonly ENRICHMENT_SUBSCRIPTIONS = [
    'Explorers by Arly',
    'Fitness by Arly',
    'Jefferson County Explorers',
    'Miami-Dade Explorers'
  ];
  public static readonly ACADEMIC_SUBSCRIPTIONS = [
    'Scholastic Summer ELA',
    'Scholastic Summer Math',
    'Scholastic Summer PreK',
    'Scholastic Extended ELA',
    'Scholastic Extended Math',
    'Scholastic Extended Plus ELA',
    'Scholastic Extended Plus Math',
    'Miami-Dade Summer',
    'NYC Summer'
  ];
  public static readonly ASSESSMENT_SUBSCRIPTIONS = [
    'Star Assessment',
    'NYC Star'
  ];
  public static readonly REVOKE_ACCESS_PAYLOAD = {
    userId: [],
    revokeDate: ''
  };
  public static readonly PROGRAMS_STATUS_DROPDOWN = [
    {
      name: 'Active Program',
      filter: 1
    },
    {
      name: 'All Program',
      filter: 0
    },
    {
      name: 'Disabled Program',
      filter: 2
    }
  ];

  public static readonly HIGHEST_ROLE = 'highest_role';
  public static readonly TEAM_ROSTER_LABEL = 'teamRoster';
  public static readonly TEAM_ROSTER_MOBILE_HEADER = {
    MOVABLE: [
      {
        name: 'First Name',
        value: 'firstName',
        action: true,
        isOpen: false,
        display: true,
        filterKey: 'firstName',
        width: '235px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Last Name',
        value: 'lastName',
        action: true,
        isOpen: false,
        display: true,
        filterKey: 'lastName',
        width: '235px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'System Role',
        value: 'systemRole',
        action: true,
        isOpen: false,
        display: true,
        filterKey: 'systemRole',
        width: '261px',
        isSort: true,
        isSearch: true,
        isSearchList: true
      },
      {
        name: 'Position',
        value: 'position',
        action: false,
        isOpen: false,
        display: true,
        filterKey: 'position',
        width: '261px',
        isSort: false,
        isSearch: false,
        isSearchList: false
      },
      {
        name: 'Programs',
        value: 'program',
        action: false,
        isOpen: false,
        display: false,
        filterKey: 'program',
        width: '261px',
        isSort: false,
        isSearch: false,
        isSearchList: false
      },
      {
        name: 'Site/Session ',
        value: 'sitesessions',
        action: true,
        isOpen: false,
        display: false,
        filterKey: 'sitesessions',
        width: '261px',
        isSort: true,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Phone',
        value: 'phoneNumber',
        action: true,
        isOpen: false,
        display: false,
        filterKey: 'phoneNumber',
        width: '261px',
        isSort: false,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Email',
        value: 'email',
        action: true,
        isOpen: false,
        display: false,
        filterKey: 'email',
        width: '261px',
        isSort: false,
        isSearch: true,
        isSearchList: false
      },
      {
        name: 'Invite Status',
        value: 'inviteStatus',
        action: true,
        isOpen: false,
        display: false,
        filterKey: 'inviteStatus',
        width: '261px',
        isSort: true,
        isSearch: true,
        isSearchList: true
      }
    ]
  };

  public static readonly VIEW_USER_DATA = {
    completeResponse: {},
    firstName: '',
    lastName: '',
    invitationStatus: '',
    invitationDate: '',
    programName: '',
    lastLogin: '',
    siteName: '',
    position: '',
    email: '',
    phone: '',
    revokeDate: '',
    systemRole: '',
    userId: '',
    selected: false
  };

  public static readonly ADD_MODE = 'add';
  public static readonly EDIT_MODE = 'edit';
  public static readonly VIEW_MODE = 'view';

  public static readonly STRING_ZERO: string = '0';
  public static ALL_ROLES = [
    'program admin',
    'session admin',
    'staff',
    'no access',
    'account admin',
    'primary account admin'
  ];
  public static USER_STATUS = {
    label: 'Status',
    id: 0,
    value: 'Active',
    required: false,
    disable: false,
    dropdownList: [
      { id: 1, value: 'Active' },
      { id: 2, value: 'Inactive' }
    ]
  };
  public static MOBILE_ERROR_SORRY_TEXT = 'We’re Sorry!';
  public static MOBILE_ERROR_MESSAGE_TEXT =
    'This page is only available on a desktop browser.';

  public static USERSTAB_HEADERDROPDOWN_SEARCHLIST = [
    {
      header: 'systemRole',
      values: [
        'Primary Account Admin',
        'Account Admin',
        'Program Admin',
        'Session Admin',
        'Staff',
        'No Access'
      ]
    },
    {
      header: 'invitationStatus',
      values: [
        '--',
        'Pending',
        'Expired',
        'Accepted',
        'Deactivated'
      ]
    }
  ];

  public static USERSTAB_HEADERDROPDOWN_SEARCHLIST_PROGRAMADMIN = [
    {
      header: 'systemRole',
      values: [
        'Program Admin',
        'Session Admin',
        'Staff',
        'No Access'
      ]
    },
    {
      header: 'invitationStatus',
      values: [
        '--',
        'Pending',
        'Expired',
        'Accepted',
        'Deactivated'
      ]
    }
  ];

  public static USERSTAB_HEADERDROPDOWN_SEARCHLIST_SESSIONADMIN = [
    {
      header: 'systemRole',
      values: ['Session Admin', 'Staff', 'No Access']
    },
    {
      header: 'invitationStatus',
      values: [
        '--',
        'Pending',
        'Expired',
        'Accepted',
        'Deactivated'
      ]
    }
  ];

  public static USERS_DROPDOWN_CONFIG = {
    label: '',
    id: 0,
    value: 'Select',
    required: true,
    disable: false,
    search: true,
    placeholder: 'Search',
    error: false,
    dropdownList: [] as IProgram[],
    checked: false,
    isAllSelect: false,
    component: 'user',
    selectDropdownCount: true
  };

  public static USERS_SINGLE_DROPDOWN_CONFIG = {
    label: '',
    id: 0,
    value: '',
    required: false,
    checked: false,
    disable: false,
    search: false,
    placeholder: 'Search',
    showSelectLabel: true,
    firstSelectLabel: 'Select',
    dropdownList: [] as {
      id: number;
      value: string;
      checked: boolean;
    }[]
  };

  public static USER = 'user';
  public static ACTION_UNDONE = 'Action undone.';
  public static SUCCESS_TEXT_BANNER =
    'Your have successfully added the privileges!';
  public static WAITLIST = 'Waitlist';
  public static PENDING = 'Pending';
  public static REJECT = 'Reject';
  public static TOTAL_SUBMITTED = 'Total Submitted';
  public static COLORS_LINE_CHART = [
    '#09729F',
    '#167A2C',
    '#4D1F7E',
    '#0E005F',
    '#C90000',
    '#FFB733',
    '#50AE41',
    '#408FB0',
    '#7C5A9D',
    '#FF5F33',
    '#A2ACBD',
    '#003F5C',
    '#00B1FF',
    '#665191',
    '#695C67',
    '#BA3F6B',
    '#3E4756',
    '#88672B',
    '#FFA600',
    '#00D3A9'
  ];

  public static readonly COLORS_CHART_BACKGROUND = [
    '#50ae41',
    '#336fac',
    '#e7eff6'
  ];
  public static ENROLLMENT_SESSION = 'Enrollment by Session';
  public static ENROLLMENT_SESSIONS_CONFIG = {
    label: '',
    id: 0,
    value: 'Select Session',
    required: false,
    disable: false,
    search: true,
    placeholder: 'Search',
    dropdownList: [],
    checked: false,
    isAllSelect: true,
    selectDropdownCount: true
  };
  public static HEADING_DATA = [
    'Total Posts',
    'Active Mobile Users',
    '  Total Direct Messages to Team Members',
    'Total Direct Messages to Guardians'
  ];
  public static TOTAL_POSTS = 'Total Posts';
  public static ACTIVE_MOBILE_USERS = 'Active Mobile Users';
  public static MESSAGES_TO_TEAM_MEMBERS =
    'Total Direct Messages to Team Members';
  public static MESSAGES_TO_Guardians =
    'Total Direct Messages to Guardians';
  public static TOTAL_POSTS_IMAGE = 'total-posts';
  public static ACTIVE_MOBILE_USERS_IMAGE =
    'active-mobile-users';
  public static MESSAGES_IMAGE = 'direct-messages';
  public static BX_FOR_TEAMS = 'BX FOR TEAMS';
  public static BX_FOR_FAMILIES = 'BX FOR FAMILIES';
  public static FAMILY_PORTAL = 'FAMILY PORTAL';
  public static TOTAL_ACTIVE_ACCOUNTS = 'Total Active Accounts';
  public static TOTAL_LIKED_POSTS = 'Total Liked Posts';
  public static TOTAL_DIRECT_MESSAGES = 'Total Direct Messages';
  public static ACTIVE_MOBILE_IMAGES = 'active-mobiles';
  public static LIKED_POSTS = 'liked-posts';
  public static ACTIVE_ACCOUNTS_IMAGE = 'total-active-accounts';
  public static NO_ENROLLMENT_EMPTY_STATE =
    'Awaiting Enrollment Data.';
  public static NO_SITE_EMPTY_STATE = 'Please select a Session';
  public static SITE_LINE_BREAK_SIZE = 14;
  public static ATTENDANCE_ADA_Groups_CONFIG = {
    label: '',
    id: 0,
    value: 'Select Group',
    required: false,
    disable: false,
    search: true,
    placeholder: 'Search',
    dropdownList: [],
    checked: false,
    isAllSelect: true,
    selectDropdownCount: true
  };
  public static NO_APPLICATION_DATA = 'No application data';
  public static AWAITING_APPLICATION_SUBMISSIONS =
    'Awaiting Application Submissions.';
  public static ATTENDANCE_SITES_CONFIG = {
    label: '',
    id: 0,
    value: 'Select Session',
    firstSelectLabel: 'Select Session',
    required: true,
    disable: false,
    search: true,
    placeholder: 'Search',
    error: false,
    dropdownList: [],
    checked: false,
    isAllSelect: false,
    selectDropdownCount: false,
    showSelectLabel: true
  };
  public static ATTENDANCE_GROUPS_CONFIG = {
    label: '',
    id: 0,
    value: 'Select Group',
    required: false,
    disable: false,
    search: true,
    placeholder: 'Search',
    dropdownList: [],
    checked: false,
    isAllSelect: false,
    selectDropdownCount: true
  };
  public static readonly SCHOLAR_ATTENDANCE_DAYS_SWITCH_TAB = [
    { name: 'Day' },
    { name: 'Week' }
  ];
  public static readonly ATTEN_COMPLETION =
    'attendence-completion';
  public static SUBMISSIONS = 'Submissions';
  public static SCHOLARS_CSV_DROPDOWN_LIST = {
    options: [
      {
        name: 'Application Submission by Status',
        key: 'Application Submission by Status',
        disable: false
      },
      {
        name: 'Total Weekly Submissions',
        key: 'Total Weekly Submissions',
        disable: false
      }
    ]
  };
  public static SCHOLARS_CSV_ATTENDANCE_LIST = {
    options: [
      {
        name: 'ADA by Session',
        key: 'ada_site',
        disable: false
      },
      {
        name: 'ADA by Week',
        key: 'ada_week',
        disable: false
      },
      {
        name: 'ADA by Day',
        key: 'ada_day',
        disable: false
      },
      {
        name: 'Daily Attendance by Scholar Checkin',
        key: 'ada_checkin',
        disable: false
      },
      {
        name: 'Daily Attendance by Scholar Checkout',
        key: 'ada_checkout',
        disable: false
      },
      {
        name: 'ADA with Scholar Details',
        key: 'ada_scholar_details',
        disable: false
      },
      {
        name: 'Daily Attendance by Scholar',
        key: 'ada_daily_attendance',
        disable: false
      }
    ]
  };
  public static AWAITING_ATTENDANCE_INPUT =
    'Awaiting Attendance Input.';
  public static NO_SITE_SESSION = 'Please select a Session.';
  public static AWAITING_ENROLLMENT = 'Awaiting Enrollment.';
  public static NO_SITE_SESSION_DATA = 'No Session data';
  public static ZERO_BAR_HEIGHT = '0.5';
  public static COMPLETION_ROUTE_NAME =
    'Scholar Attendance Completion Analytics';
  public static NO_QUALITY_DATA =
    'There is currently no quality data available.';
  public static SCHOLARS_CSV_COMPLETION_LIST = {
    options: [
      {
        name: 'Completion by Session',
        key: 'com_site',
        disable: false
      },
      {
        name: 'Completion by Week',
        key: 'com_week',
        disable: false
      },
      {
        name: 'Completion by Day',
        key: 'com_day',
        disable: false
      }
    ]
  };
  public static readonly LOWERCASE_MSG =
    'At least 1 lowercase letter (a-z)';
  public static readonly CAPITAL_MSG =
    'At least 1 uppercase letter (A-Z)';
  public static readonly TEN_CHARACTER_MSG =
    'At least 10 characters and no more than 15 characters';
  public static readonly SPECIAL_CHAR_MSG =
    "At least 1 special character (!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~)";
  public static readonly ONE_NUMBER = 'At least 1 number';
  public static readonly ENTER_PASS: string = 'enter-pass';
  public static readonly RESET_PASSWORD_INDICATOR_STRIP =
    'resetPasswordStripIndicator';
  public static readonly RESET_PASSWORD = 'resetPassword';
  public static readonly PASSWORD_RESET_CONFIRMATION_TEXT =
    'You have successfully reset the password.';
  public static ADD_PRIVILEGES_ACTION = 'Add Privileges';
  public static SEND_INVITATION_ACTION = 'Send Invitation';
  public static RESET_PASSWORD_ACTION = 'Reset Password';
  public static PROF_LEARNING_COURSES_CONFIG = {
    label: '',
    id: 0,
    value: 'Select Courses',
    required: false,
    disable: false,
    search: true,
    placeholder: 'Search',
    dropdownList: [],
    checked: false,
    isAllSelect: true,
    selectDropdownCount: true
  };
  public static PROF_LEARNING_ROLES_CONFIG = {
    label: '',
    id: 0,
    value: 'All System Roles',
    required: false,
    disable: false,
    search: false,
    placeholder: 'Search',
    dropdownList: [],
    checked: true,
    isAllSelect: true,
    selectDropdownCount: true
  };
  public static PROF_LEARNING_ROLES_LIST = [
    {
      id: 5,
      value: 'Program Admin',
      checked: false,
      color: '#ed589d',
      count: 9
    },
    {
      id: 6,
      value: 'Session Admin',
      checked: false,
      color: '#6c63f0',
      count: 5
    },
    {
      id: 7,
      value: 'Staff',
      checked: false,
      color: '#3aacff',
      count: 2
    }
  ];
  public static NO_CATEGORY_SELECTED = 'Please select courses.';
  public static AWAITING_COURSES =
    'Awaiting Course Completions.';
  public static readonly DEFAULT_COLLECTION_NAME = 'General';
  public static PROF_LEARNING_DATA = {
    labels: [],
    datasets: []
  };

  public static readonly DOWNLOAD_CONSOLIDATED_ROSTER_CSV_KEY = 'cons_roster';

  public static SCHOLARS_CSV_RETENSION_LIST = {
    options: [
      {
        name: 'Retention by Session',
        key: 'ret_site',
        disable: false
      },
      {
        name: 'Retention by Week',
        key: 'ret_week',
        disable: false
      },
      {
        name: 'Retention by Day',
        key: 'ret_day',
        disable: false
      }
    ]
  };
  public static SCHOLARS_CSV_ENROLLMENT_LIST = {
    options: [
      {
        name: 'Enrollment CSV',
        key: 'en_site',
        disable: false
      },
      {
        name: 'Consolidated Scholar Roster CSV',
        key: APP_CONST.DOWNLOAD_CONSOLIDATED_ROSTER_CSV_KEY,
        disable: false
      }
    ]
  };

  public static CONTENTFUL_GRAPHQL_URL =
    process.env.VUE_APP_BASE_CONTENTFUL_GRAPHQL_URL;
  public static CONTENTFUL_GRAPHQL_SPACE =
    process.env.VUE_APP_BASE_CONTENTFUL_GRAPHQL_SPACE;
  public static CONTENTFUL_GRAPHQL_ENV =
    process.env.VUE_APP_BASE_CONTENTFUL_GRAPHQL_ENV;
  public static CONTENTFUL_GRAPHQL_TOKEN =
    process.env.VUE_APP_BASE_CONTENTFUL_GRAPHQL_TOKEN;

  public static ALGOLIA_INDEX_NAME =
    process.env.VUE_APP_BASE_ALGOLIA_INDEX_NAME;
  public static ALGOLIA_APP_ID =
    process.env.VUE_APP_BASE_ALGOLIA_APP_ID;
  public static ALGOLIA_API_KEY =
    process.env.VUE_APP_BASE_ALGOLIA_API_KEY;

  public static CARD_CONTENT_TYPES_WITH_BADGES = [
    'CONTENT_PREVIEW',
    'LEARN_ABOUT'
  ];

  public static readonly SCHOLAR_FAMILY_PAYLOAD = {
    accountId: 0,
    count: 25,
    page: 1,
    sortDir: 0,
    sortField: 'firstName',
    searchField: '',
    search: '',
    globalSearch: '',
    isExactMatch: false,
  };

  public static readonly ALLOWED_ROLES_FOR_USER_MANAGEMENT = [
    APP_CONST.ROLES.BX_SUPER_ADMIN,
    APP_CONST.ROLES.BX_ADMIN,
    APP_CONST.ROLES.ACCOUNT_ADMIN,
    APP_CONST.ROLES.PROGRAM_ADMIN,
    APP_CONST.ROLES.SESSION_ADMIN
  ];

  // Discounts temporary added for mocking porpuses
  // TODO: delete once the real discounts are implemented
  public static readonly MOCKED_DISCOUNTS: Discount[] = [
    {
      id: 1,
      appliesTo: 'Account',
      discountTypeId: DiscountType.EARLY_REGISTRATION,
      description: 'Lorem ipsum dolor sit amet consectetur adipisicing elit. Similique quibusdam obcaecati quas? Dicta dolor est ipsum alias voluptatem odio, ullam numquam aspernatur at atque iusto vitae quod sapiente aut et?',
      amountUnitType: AmountUnitType.FIXED_AMOUNT,
      name: 'early discount name',
      rules: [{ amount: 350, timeRangeType: TimeRangeType.DAYS, timeRangeUnit: 3 }],
      isActive: true,
      accountId: 1
    },
    {
      id: 2,
      appliesTo: 'Account',
      discountTypeId: DiscountType.MULTI_PARTICIPANT,
      description: 'Lorem ipsum dolor sit amet consectetur adipisicing elit. Similique quibusdam obcaecati quas? Dicta dolor est ipsum alias voluptatem odio, ullam numquam aspernatur at atque iusto vitae quod sapiente aut et?',
      amountUnitType: AmountUnitType.PERCENT_RATE,
      name: 'multi participant name',
      rules: [
        { amount: 20, participants: 3 },
        { amount: 25, participants: 4 },
        { amount: 35, participants: 5 }
      ],
      isActive: true,
      accountId: 1
    }
  ];

  public static readonly GRADES = ['PK', 'K', '1', '2', '3', '4', '5', '6', '7', '8'];
  public static readonly LOCAL_STORAGE_KEYS = {
    attendance: {
      rowsPerPage: 'attendanceRowsPerPage',
    }
  };
}

